import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from "react";
import {
  Trash2,
  Download,
  Settings,
  ChevronUp,
  ChevronDown,
  Plus,
  AlertTriangle,
  Palette,
  Sun,
  Moon,
  Check,
  Upload,
  X,
} from "lucide-react";
import {
  api,
  // 174 §1.2 — the three headers api() sends, for the three calls below that
  // api() cannot carry: one blob download and two multipart uploads.
  authHeaders,
  getToken,
  getKillState,
  getDocTemplateVariables,
  getDocTemplates,
  uploadDocTemplateVariant,
  deleteDocTemplateVariant,
  downloadDocTemplateVariant, // 204 §E
} from "../api";
import type { KillState } from "../api";
import { srvErrText } from "../lib/srvErr"; // 195 §D.2 — the export download reads the server's reason
import { useTranslation, localeFor } from "../i18n"; // 198 §D — the month names in the reader's locale
import { roleLabel } from "../hooks/useRoleLabels";
import SettingsTaxonomies from "./SettingsTaxonomies";
import SettingsRates from "../components/SettingsRatesPanel";
import TimeField from "../components/TimeField"; // 117 §5
import DocBillingActivitiesPanel from "../components/DocBillingActivitiesPanel";
import InvoiceExportFormatPanel from "../components/InvoiceExportFormatPanel";
import { usePreferences } from "../hooks/usePreferences";
import { usePermissions } from "../hooks/usePermissions";
import { I, SK, sv, pname, LINK_ICON_OPTIONS } from "../utils";
import { isActiveCo } from "../lib/collaboratorState";
import ThemeManager, { WindowChromeCard } from "../components/ThemeManager";

// 169 R3 — every ISO 4217 code this runtime knows, in its own order (the firm's
// is put first by the field); the four-code fallback where the API is missing.
const CURRENCY_OPTIONS: ReadonlyArray<readonly [string, string]> = (() => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  let codes = ["EUR", "USD", "GBP", "CHF"];
  try {
    if (typeof intl.supportedValuesOf === "function") codes = intl.supportedValuesOf("currency");
  } catch {
    /* the fallback list stands */
  }
  return codes.map((c) => [c, c] as const);
})();
import RolesAdminPanel from "../components/RolesAdminPanel";
import AuthFactorsCard from "../components/AuthFactorsCard";
import AuthAttemptsCard from "../components/AuthAttemptsCard";
import type { ToastFn } from "../components/Toast"; // 213 — the two lazy panes' toast
/* 213 §1, §4 — « Mes connexions » and the administrator's shortcut links:
   LOADED ON USE, one chunk for both, mounted only while their pane is open —
   so the key a mint shows once goes when the pane closes. */
const DoorKeysPanel = lazy(() => import("../components/DoorKeysPanel"));
const ShortcutLinksPanel = lazy(() => import("../components/DoorKeysPanel").then((m) => ({ default: m.ShortcutLinksPanel })));
import AuthIncidentPanel from "../components/AuthIncidentPanel";
import FirmKillSwitchPanel from "../components/FirmKillSwitchPanel";
// BRIEF 103 §2c — the third panel of the admin security block.
import InvitationPolicyPanel from "../components/InvitationPolicyPanel";
// 81 §8 — the COMPONENT is superseded by AuthIncidentPanel and no longer
// mounted; `CollaboratorLike` is still declared in that file and still used,
// which is register row 81e.
import type { CollaboratorLike } from "../lib/authShapes";
import CaseTeamRightsPanel from "../components/CaseTeamRightsPanel";
import SurfaceScopesPanel from "../components/SurfaceScopesPanel";
import FolderTemplatesPanel from "../components/FolderTemplatesPanel";
import DocTemplatesPanel, { DocTemplateVariantSlots } from "../components/DocTemplatesPanel";
import type { DocumentTemplate, DocumentTemplateVariant } from "../../shared/entities";
import ProformaXmlTemplateEditor from "../components/ProformaXmlTemplateEditor";
import PlacesAdminPanel from "../components/PlacesAdminPanel";
import ScheduleAxisPanel from "../components/ScheduleAxisPanel";
import { resolveAccountingYearStart, daysInStartMonth } from "../lib/accountingYear.mjs"; // 198 §D — the firm's accounting year, the one rule
import type { Translate } from "../lib/interestRefusal"; // 198 §D — the translator's one shape
import { deriveDocumentPalette, dayThemeVarsOf } from "../lib/themeDerivation"; // 204 §A.3 — the swatch reads the firm's colour as the documents and the wordmark derive it
import {
  BackupDestinationsPanel,
  SettingsXlsxHubPanel,
} from "../components/Phase4SettingsPanels";
import { CT, RAD, FS, Z } from "../lib/tokens";
import { ModalLayout, ListNotLoaded } from "../components/SharedUI";
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad"; // 213 §11.3 — the Microsoft 365 status, asked the 210 §1 way
import { oneRow, usePageListContext, useReadMark } from "../hooks/usePageList";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import FieldSelect from "../components/FieldSelect"; // 207 §12 — the firm's country, picked from the country list
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy"; // 207 §12 — the list the parties use
import { FIRM_ADDRESS_LANGS } from "../lib/firmAddress.mjs"; // 207B §4 — the firm's address, one block per language
import { firmSettingsTyped } from "../lib/firmSettingsSaves"; // 207B §5 — what was typed and not yet answered
import { resolveTaxonomyEntry } from "../lib/taxonomyHelpers"; // 207B §4 — the firm's country, by the one matcher
import { pickEntryLabelIn } from "../utils/taxonomyMap"; // 207B §4 — …as a word in each block's language
import type { TaxList } from "../lib/taxonomyTypes";
// BRIEF 108 §1 — the six WhatsApp rates. The keys and the defaults come from
// shared/whatsappRates.json, which the server calculator reads too.
import {
  readWhatsappRates,
  whatsappRatePayload,
  asWhatsappDestination,
  WHATSAPP_DESTINATION_KEY,
  type WhatsappRateForm,
  type WhatsappDestination,
} from "../lib/whatsappRates";
import {
  APP_FONTS,
  DEFAULT_APP_FONT,
  SYSTEM_FONT,
  appFontStack,
} from "../lib/appFonts";
import { OUTLOOK_PRESET_COLORS } from "../lib/outlookPresetColors"; // 181 A.5

// Phase 33 (Change C) — Outlook's 25-preset category-colour palette. 181 A.5
// moved the table to `src/lib/outlookPresetColors.ts`, because the e-mail
// row's dots read the same colours: one table, two readers.

// Phase 34 §8 — a narrow custom dropdown that shows a colour swatch on EVERY row
// of the OPEN list (the native <select> can only swatch the closed field). 25
// Outlook presets, no arbitrary colour wheel. Theme tokens only.
function OutlookColorSelect({ value, onChange }: any) {
  const [open, setOpen] = useState(false);
  const cur =
    OUTLOOK_PRESET_COLORS.find((c) => c.id === value) || OUTLOOK_PRESET_COLORS[0];
  return (
    <div style={{ position: "relative", flex: 1 }}>
      <button
        type="button"
        className="field-input"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 14,
            height: 14,
            borderRadius: RAD.r3,
            flex: "0 0 auto",
            border: "1px solid var(--bd)",
            background: cur.hex,
          }}
        />
        <span style={{ flex: 1 }}>{cur.name}</span>
        <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>▾</span>
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: Z.tip }}
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 2px)",
              left: 0,
              right: 0,
              zIndex: Z.tipMenu,
              maxHeight: 240,
              overflowY: "auto",
              background: "var(--bi)",
              border: "1px solid var(--bd)",
              borderRadius: RAD.r6,
              boxShadow: "0 8px 24px rgba(0,0,0,.35)",
            }}
          >
            {OUTLOOK_PRESET_COLORS.map((c) => (
              <div
                key={c.id}
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 8px",
                  cursor: "pointer",
                  background: c.id === value ? "var(--as)" : "transparent",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: RAD.r3,
                    flex: "0 0 auto",
                    border: "1px solid var(--bd)",
                    background: c.hex,
                  }}
                />
                <span style={{ fontSize: FS.s12 }}>{c.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// One alias, not seven `: any` signatures — the panels mirror SettingsView's own
// untyped-props style; the ratchet counts AnyKeyword nodes, and the debt is
// documented HERE, once, instead of growing by seven.
type SettingsPanelProps = Record<string, any>;

/* 170 §3e — the served pro-forma template row, as this panel reads it. The
   generated `DocumentTemplate` has no `variants` because Prisma's scalar shape
   has none; the API includes them. */
type PfTemplate = DocumentTemplate & { variants?: DocumentTemplateVariant[] };

// ── BRIEF 163 §B.1 — THE GATED IIFES BECOME CHILD COMPONENTS ────────────────
// Seven permission-gated JSX IIFEs inside SettingsView held 29 hooks between
// them — every one conditional by construction (the gate decided whether the
// IIFE ran at all). The one shape of the fix: each IIFE that contains a hook
// becomes a child component at module scope (stable identity — Bug-A), its
// gate stays at the mount site, its closure arrives as props, its body is
// verbatim. Two internal `return null` guards (RatesAdminBlock, I18nDataPanel)
// moved out to their mount-site gates for the same reason.

function BillingFolderPanels({
  cls,
  sh4,
  ex,
  toast,
  firmLogo,
  firmName,
  firmSettings,
  saveFirmSettings,
  applyFirmSettings,
  sg,
}: SettingsPanelProps) {
  /* 198 §D — the translator narrowed ONCE at this component's seam (181's rule:
     every literal `_t(…)` stays visible to i18n-keys-complete and stops costing
     the ratchet an unsafe call); the i18n handle typed for the one member the
     accounting-year month names read. */
  const { t: _t } = useTranslation() as { t: Translate };
  const _ayI18n = useTranslation() as { lang: string };
  /* 207 §12 [RULED] — the firm's country is a pick from the country list the
     parties use (the `country` kind), stored as its CODE and shown in the
     reader's language; the tuples for the options, the entries for the one
     matcher's provisional mark on a text a firm typed before. */
  const firmCountryList = useTaxonomyList("country") as [string, string][];
  const firmCountryEntries: TaxList = (useTaxonomy("country") as { entries: TaxList }).entries;
        // A2: firmSettings is the server-backed singleton; fsLocal mirrors it so
        // controlled inputs stay responsive while saveFirmSettings PUT is in flight.
        const [fsLocal, setFsLocal] = useState(firmSettings || {});
        /* 207B §5 — App's row copied over the pane's copy with what was typed
           and not yet answered laid over it, read when the update RUNS: a key
           typed between the render that brought the row and this effect is in
           the queue by then (src/lib/firmSettingsSaves.ts). Copied as it came,
           it put the older text back under the caret (WebKit, measured). */
        useEffect(() => {
          setFsLocal(() => ({ ...(firmSettings || {}), ...firmSettingsTyped() }));
        }, [firmSettings]);
        const inv = fsLocal;
        /* 170 §3f — THE MERGE-MARKER VOCABULARY, FROM ITS ONE REGISTRY.
           `docGenVariables.js` INVOICE_SCALARS is served at
           GET /api/doc-templates/variables, and `DocTemplatesPanel` has always
           read it from there. Two hand-copies existed beside it — this panel's
           and `ProformaXmlTemplateEditor`'s — and they had drifted in opposite
           directions: this one carried five markers the resolver does not emit,
           the editor's was missing five it does. Both are deleted; the list is
           fetched once here and handed down, so there is one owner and a fourth
           copy cannot start.
           Failure is silent on purpose: an empty vocabulary renders an empty
           reference list, which is what a reader of a broken endpoint should
           see rather than a stale hard-coded promise. */
        const [invoiceMarkers, setInvoiceMarkers] = useState<{ tag: string; desc: string }[]>([]);
        useEffect(() => {
          let live = true;
          getDocTemplateVariables()
            .then((v: any) => {
              if (!live) return;
              const rows = Array.isArray(v?.invoiceScalars) ? v.invoiceScalars : [];
              setInvoiceMarkers(rows.map((r: any) => ({ tag: String(r.tag), desc: String(r.desc || "") })));
            })
            .catch(() => { if (live) setInvoiceMarkers([]); });
          return () => { live = false; };
        }, []);
        /* 170 §3e — THE PRO-FORMA TEMPLATES THE PDF PATH ACTUALLY READS.
           `invoices.js:1509-1525` resolves a `DocumentTemplate` BY KIND —
           'invoice', or the document's own kind for a retainer or a credit — and
           merges its per-language variant. Those are the rows this panel now
           holds, in place of the `FirmSettings.proformaTemplate` blob nothing
           ever read. */
        /* TYPED THROUGHOUT, because `scripts/lint/ratchets.js` counts `any`
           NODES per file and forbids growth — eslint headroom buys nothing
           there, which a first cut of this learned the hard way. */
        const PF_KINDS = ["invoice", "retainer", "credit"];
        const [pfTpls, setPfTpls] = useState<PfTemplate[]>([]);
        const [pfBusy, setPfBusy] = useState(false);
        const loadPfTpls = useCallback(() => {
          getDocTemplates()
            .then((rows: unknown) => {
              const all: PfTemplate[] = Array.isArray(rows) ? (rows as PfTemplate[]) : [];
              setPfTpls(all.filter((r) => PF_KINDS.includes(String(r.kind))));
            })
            .catch(() => { setPfTpls([]); });
        }, []);
        useEffect(() => { loadPfTpls(); }, [loadPfTpls]);
        const pfErr = (e: unknown, fb: string) => {
          const m = (e as { message?: string } | null)?.message;
          return typeof m === "string" && m ? m : fb;
        };
        const pfUpload = async (tpl: PfTemplate, lang: string, file: File | undefined) => {
          if (!file) return;
          if (!/\.docx$/i.test(file.name)) {
            toast("⚠ " + _t("docTpl.only_docx", { fallback: "Only .docx files are supported" }));
            return;
          }
          setPfBusy(true);
          try {
            await uploadDocTemplateVariant(tpl.id, lang, file);
            loadPfTpls();
            toast(_t("docTpl.uploaded", { fallback: "Template file uploaded" }));
          } catch (e: unknown) {
            toast(pfErr(e, _t("docTpl.upload_failed", { fallback: "Upload failed" })), "error");
          } finally { setPfBusy(false); }
        };
        const pfRemove = async (tpl: PfTemplate, lang: string) => {
          setPfBusy(true);
          try { await deleteDocTemplateVariant(tpl.id, lang); loadPfTpls(); }
          catch (e: unknown) { toast(pfErr(e, _t("docTpl.delete_failed", { fallback: "Delete failed" })), "error"); }
          finally { setPfBusy(false); }
        };
        // 204 §E — the same row, the same third verb as DocTemplatesPanel's (union, not a fork)
        const pfDownload = async (tpl: PfTemplate, lang: string, fileName: string) => {
          try { await downloadDocTemplateVariant(tpl.id, lang, fileName); }
          catch (e: unknown) { toast(pfErr(e, _t("docTpl.download_failed", { fallback: "Download failed" })), "error"); }
        };
        const updInv = (patch: any) => {
          setFsLocal((p: any) => ({ ...(p || {}), ...patch }));
          if (saveFirmSettings) saveFirmSettings(patch).catch(() => {});
        };
        const firm = inv.firm || {};
        const updFirm = (patch: any) => updInv({ firm: { ...firm, ...patch } });
        /* 207B §4 [RULED 2026-09-24, ruling 1 — Laurent: "why don't just copy
           the system for eg general conditions (3 adress boxes)?"] — THE FIRM'S
           ADDRESS IS ONE WHOLE ADDRESS PER LANGUAGE: French, Dutch, English,
           stacked like the general conditions, each its street and number,
           postal code and city (`FirmSettings.firm.addressByLang`,
           src/lib/firmAddress.mjs — the one door every reader goes through) and
           ending with the country's word in its own language, greyed: a block
           reads exactly as a document prints it. 207 §12's single street and
           city with three variant boxes under each are gone (they never
           reached prod). The country stays ONE pick, below the blocks. */
        const firmCountryStored = (firm as Record<string, unknown>).country;
        const firmCountryValue = typeof firmCountryStored === "string" ? firmCountryStored : "";
        type FirmAddrBlock = { street?: string; postalCode?: string; city?: string };
        const firmAddrRaw = (firm as Record<string, unknown>).addressByLang;
        const firmAddrMap = (firmAddrRaw && typeof firmAddrRaw === "object" && !Array.isArray(firmAddrRaw) ? firmAddrRaw : {}) as Record<string, FirmAddrBlock>;
        const firmLangName: Record<string, string> = {
          fr: _t("settings.gc.french", { fallback: "French" }),
          nl: _t("settings.gc.dutch", { fallback: "Dutch" }),
          en: _t("settings.gc.english", { fallback: "English" }),
        };
        // The country's word in a block's language — docWords.js `firmCountryWord`'s
        // rule: the one matcher, `other` names no country, an unmatched text as typed.
        const firmCountryIn = (l: string): string => {
          if (!firmCountryValue) return "";
          const hit = resolveTaxonomyEntry(firmCountryValue, firmCountryEntries) as { code?: unknown } | null;
          const country = hit && typeof hit.code === "string" ? hit.code : "";
          if (country === "other") return "";
          return hit ? String(pickEntryLabelIn(hit, l)) : firmCountryValue;
        };
        /* 198 §D.1 — the accounting-year start as the firm holds it (the local
           copy `inv` carries the served columns; absent → 1 January), and the
           reader's month names through the app's own locale. */
        const _ayStart = resolveAccountingYearStart(inv);
        const _ayMonthFmt = new Intl.DateTimeFormat(localeFor(_ayI18n.lang), { month: "long" });
        const _ayMonthName = (m: number): string => {
          try {
            return _ayMonthFmt.format(new Date(2000, m - 1, 1));
          } catch {
            return String(m);
          }
        };
        const banks = inv.bankAccounts || [];
        const [bankLocked, setBankLocked] = useState(true);
        const [bankForm, setBankForm] = useState<any>(null);
        const saveBk = () => {
          if (!bankForm?.iban?.trim()) {
            toast(
              _t("settings.toast.ibanRequired", {
                fallback: "IBAN required",
              }),
            );
            return;
          }
          if (bankForm._idx != null) {
            const u = [...banks];
            u[bankForm._idx] = {
              name: bankForm.name,
              iban: bankForm.iban,
              bic: bankForm.bic,
              bankName: bankForm.bankName,
            };
            updInv({ bankAccounts: u });
          } else {
            updInv({
              bankAccounts: [
                ...banks,
                {
                  name: bankForm.name || "",
                  iban: bankForm.iban,
                  bic: bankForm.bic || "",
                  bankName: bankForm.bankName || "",
                },
              ],
            });
          }
          setBankForm(null);
          toast(_t("settings.toast.bankSaved", { fallback: "Saved" }));
        };
        const delBk = (i: any) => {
          updInv({ bankAccounts: banks.filter((_: any, j: any) => j !== i) });
          toast(_t("settings.toast.bankRemoved", { fallback: "Removed" }));
        };
        const pfx = inv.proformaPrefix || "PF";
        // 179 C.9 — `startN` and the `preview` string STOOD HERE: the "Starting
        // number" field and its preview described a numbering the server never
        // ran (the allocator is `proformaCounter`, lifetime, and never read
        // startingNumber). Both go; the column follows in C.9b.
        // Phase 35 (C1 / L2) — opt-in manual pro-forma-number override.
        const allowManualNum = !!inv.proformaAllowManualNumber;
        const dueDays = inv.dueDays ?? 30;
        const vatRate = inv.vatRate ?? 21;
        const costsRatio = inv.costsRatio ?? 19;
        const gcLangs = ["nl", "fr", "en", "de"];
        const gcLabels: Record<string, string> = {
          nl: "Dutch",
          fr: "French",
          en: "English",
          de: "German",
        };
        const gc = inv.generalConditions || {};
        // Phase 40 (D10) — trilingual pro-forma draft-email body (JSON lang-map).
        // Defensive: a legacy String? value (pre-migration) reads as empty.
        const pet =
          inv.proformaEmailTemplate &&
          typeof inv.proformaEmailTemplate === "object"
            ? inv.proformaEmailTemplate
            : {};
        const invLogoPreview = inv.logo || null;
        const handleLogo = (e: any) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (file.size > 512000) {
            toast(_t("settings.toast.max500", { fallback: "⚠ Max 500KB" }));
            e.target.value = "";
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            const b64 = (ev.target as any).result;
            updInv({ logo: b64 });
          };
          reader.readAsDataURL(file);
          e.target.value = "";
        };
        return (
          <>
            {sg("settings.billing.firm") && (
            <div
              className={`detail-card settings-pane${cls("invFirm")}`}
              style={{ order: 61 }}
            >
              {sh4(
                "invFirm",
                _t("settings.panel.firmDetails", {
                  fallback: "Firm Details",
                }),
              )}
              <div className="field">
                <label>
                  {_t("settings.label.firmName", { fallback: "Firm Name" })}
                </label>
                <input autoComplete="off" /* 207 §18 */
                  className="field-input no-contact-autofill"
                  value={firm.name || firmName || ""}
                  onChange={(e) => updFirm({ name: e.target.value })}
                  placeholder={_t("settings.label.firmName", {
                    fallback: "Firm Name",
                  })}
                />
              </div>
              {/* 207B §4 [RULED] — one whole address per language, stacked like
                  the general conditions; three inputs in one row, kept apart
                  (an e-invoice wants them apart), then the country's word in
                  that language, greyed, not editable. §18's treatment on each
                  input: the firm's address is not a person's contact card. */}
              {FIRM_ADDRESS_LANGS.map((l) => {
                const b = firmAddrMap[l] ?? {};
                const setB = (k: "street" | "postalCode" | "city", v: string) => {
                  updFirm({
                    addressByLang: {
                      ...firmAddrMap,
                      [l]: { street: b.street ?? "", postalCode: b.postalCode ?? "", city: b.city ?? "", [k]: v },
                    },
                  });
                };
                const streetW = _t("settings.label.street", { fallback: "Street" });
                const postalW = _t("settings.label.postalCode", { fallback: "Postal Code" });
                const cityW = _t("settings.label.city", { fallback: "City" });
                return (
                  <div key={l} className="field" data-firm-address={l}>
                    <label>
                      {_t("field.address", { fallback: "Address" })} — {firmLangName[l]}
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 0.8fr) minmax(0, 1.3fr) auto",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <input
                        autoComplete="off"
                        className="field-input no-contact-autofill"
                        value={b.street ?? ""}
                        placeholder={streetW}
                        aria-label={`${streetW} — ${firmLangName[l]}`}
                        onChange={(e) => {
                          setB("street", e.target.value);
                        }}
                      />
                      <input
                        autoComplete="off"
                        className="field-input no-contact-autofill"
                        value={b.postalCode ?? ""}
                        placeholder={postalW}
                        aria-label={`${postalW} — ${firmLangName[l]}`}
                        onChange={(e) => {
                          setB("postalCode", e.target.value);
                        }}
                      />
                      <input
                        autoComplete="off"
                        className="field-input no-contact-autofill"
                        value={b.city ?? ""}
                        placeholder={cityW}
                        aria-label={`${cityW} — ${firmLangName[l]}`}
                        onChange={(e) => {
                          setB("city", e.target.value);
                        }}
                      />
                      <span style={{ fontSize: FS.s10_5, color: "var(--tm)", whiteSpace: "nowrap" }}>
                        {firmCountryIn(l)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {/* 207 §12 / §13 — a pick from the country list, stored as its code;
                  it shows what is stored — the « Belgium » it showed when empty
                  was never saved, and is gone. */}
              <FieldSelect
                label={_t("settings.label.country", { fallback: "Country" })}
                value={firmCountryValue}
                onChange={(v) => {
                  updFirm({ country: v });
                }}
                options={[["", `— ${_t("common.none", { fallback: "None" })} —`], ...firmCountryList]}
                searchable
                entries={firmCountryEntries}
                t={_t}
              />
              <div className="field-row">
                <div className="field">
                  <label>
                    {_t("settings.label.vatNumber", {
                      fallback: "VAT Number",
                    })}
                  </label>
                  <input
                    className="field-input"
                    value={firm.vat || ""}
                    onChange={(e) => updFirm({ vat: e.target.value })}
                    placeholder="BE 0xxx.xxx.xxx"
                  />
                </div>
                <div className="field">
                  <label>
                    {_t("settings.label.currency", { fallback: "Currency" })}
                  </label>
                  {/* 169 R3 — THE FIRM'S CURRENCY LIVES IN THE FIRM IDENTITY
                      BLOCK (FirmSettings.firm.currency, ISO 4217, absent = EUR),
                      beside the VAT number the documents print. Set once per
                      firm; the same `firm` PUT the other fields ride. */}
                  <MenuSelect
                    className="field-input"
                    value={firm.currency || "EUR"}
                    onChange={(v) => updFirm({ currency: v })}
                    options={[
                      [firm.currency || "EUR", firm.currency || "EUR"] as const,
                      ...CURRENCY_OPTIONS.filter((o) => o[0] !== (firm.currency || "EUR")),
                    ]}
                    ariaLabel={_t("settings.label.currency", { fallback: "Currency" })}
                  />
                </div>
                <div className="field">
                  <label>
                    {_t("settings.label.phone", { fallback: "Phone" })}
                  </label>
                  <input
                    className="field-input"
                    value={firm.phone || ""}
                    onChange={(e) => updFirm({ phone: e.target.value })}
                  />
                </div>
              </div>
              {/* 198 §D.1 [RULED — Laurent, 2026-09-19] — THE FIRM'S ACCOUNTING
                  YEAR STARTS ON: a day and a month, 1 January unless set — the
                  pair every period picker's « Exercice comptable en cours /
                  précédent » resolves against (periodRange.ts, D.3).
                  198-bis §C [RULED — Laurent, 2026-09-20: "in Firm details,
                  together with VAT number etc — nothing to do with payments"]:
                  MOVED here from the Payment Terms pane, after « Numéro de
                  TVA » / « Devise », under THIS pane's gate
                  (`settings.billing.firm`, the route's FIELD_TO_GATE too).
                  C.1 — THE DATA DID NOT MOVE: the two columns are TOP-LEVEL
                  FirmSettings columns (the §D.1 migration's), written through
                  `updInv` exactly as before — never through `updFirm`'s `firm`
                  blob its new neighbours use; `resolveAccountingYearStart(inv)`
                  reads `inv`, not `inv.firm`. Two house MenuSelects: the day
                  list is the month's own length — the rule is
                  src/lib/accountingYear.mjs's, February 28 (a start must be a
                  day every year has); a month change that strands the day
                  sends both, clamped, so the server never sees a pair it would
                  refuse. The month names are the reader's locale's. */}
              <div className="field">
                <label>
                  {_t("settings.label.accountingYearStart", { fallback: "Accounting year starts on" })}
                </label>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <MenuSelect
                    className="field-input"
                    style={{ width: 72 }}
                    ariaLabel={_t("settings.label.accountingYearStartDay", { fallback: "Day" })}
                    title={_t("settings.label.accountingYearStartDay", { fallback: "Day" })}
                    value={String(_ayStart.day)}
                    onChange={(v) => {
                      updInv({ accountingYearStartDay: Number(v) });
                    }}
                    options={Array.from({ length: daysInStartMonth(_ayStart.month) }, (_, i) => [String(i + 1), String(i + 1)] as const)}
                  />
                  <MenuSelect
                    className="field-input"
                    style={{ width: 160 }}
                    ariaLabel={_t("settings.label.accountingYearStartMonth", { fallback: "Month" })}
                    title={_t("settings.label.accountingYearStartMonth", { fallback: "Month" })}
                    value={String(_ayStart.month)}
                    onChange={(v) => {
                      const month = Number(v);
                      const day = Math.min(_ayStart.day, daysInStartMonth(month));
                      updInv(day === _ayStart.day ? { accountingYearStartMonth: month } : { accountingYearStartMonth: month, accountingYearStartDay: day });
                    }}
                    options={Array.from({ length: 12 }, (_, i) => [String(i + 1), _ayMonthName(i + 1)] as const)}
                  />
                </div>
                <p style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 4, lineHeight: 1.5 }}>
                  {_t("settings.help.accountingYearStart", {
                    fallback:
                      "1 January unless set. « Current accounting year » on every period picker then runs from the last start on or before today to the day before the next; « previous » is the twelve months before it.",
                  })}
                </p>
              </div>
              <div className="field-row">
                <div className="field">
                  <label>
                    {_t("settings.label.email", { fallback: "Email" })}
                  </label>
                  <input
                    className="field-input"
                    value={firm.email || ""}
                    onChange={(e) => updFirm({ email: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>
                    {_t("settings.label.website", { fallback: "Website" })}
                  </label>
                  <input
                    className="field-input"
                    value={firm.website || ""}
                    onChange={(e) => updFirm({ website: e.target.value })}
                  />
                </div>
              </div>
            </div>
            )}
            {sg("settings.billing.bank") && (
            <div
              className={`detail-card settings-pane${cls("invBank")}`}
              style={{ order: 62 }}
            >
              {sh4(
                "invBank",
                _t("settings.panel.bankAccounts", {
                  fallback: "Bank Accounts",
                }),
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                {!bankLocked && (
                  <button
                    className="btn btn-primary"
                    style={{ padding: "2px 7px", fontSize: FS.s10 }}
                    onClick={() =>
                      setBankForm({
                        name: "",
                        iban: "",
                        bic: "",
                        bankName: "",
                      })
                    }
                  >
                    {_t("settings.btn.add", { fallback: "+ Add" })}
                  </button>
                )}
                <span style={{ marginLeft: "auto" }} />
                <button
                  type="button"
                  className="link-btn"
                  title={bankLocked ? "Unlock" : "Lock"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: bankLocked ? "none" : "rgba(251,146,60,.13)",
                    border: bankLocked
                      ? "1px solid transparent"
                      : "1px solid rgba(251,146,60,.35)",
                    borderRadius: RAD.r6,
                    cursor: "pointer",
                    padding: "2px 4px",
                    lineHeight: 1,
                    animation: bankLocked
                      ? undefined
                      : "lkp 2s ease-in-out infinite",
                  }}
                  onClick={() => setBankLocked((v) => !v)}
                >
                  {bankLocked ? (
                    <Settings size={I.m} style={{ color: "var(--ts)" }} />
                  ) : (
                    <Settings size={I.m} style={{ color: "var(--or)" }} />
                  )}
                </button>
              </div>
              {banks.length === 0 && (
                <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                  {_t("settings.bank.empty", {
                    fallback: "No bank accounts configured.",
                  })}
                </p>
              )}
              {banks.map((b: any, i: any) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 0",
                    borderBottom: "1px solid rgba(255,255,255,.04)",
                    fontSize: FS.s11,
                  }}
                >
                  <span style={{ fontWeight: 600, minWidth: 80 }}>
                    {b.name ||
                      _t("settings.bank.account", { fallback: "Account" })}
                  </span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: FS.s10,
                      color: "var(--ts)",
                      flex: 1,
                    }}
                  >
                    {b.iban}
                  </span>
                  <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                    {b.bic}
                  </span>
                  <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                    {b.bankName}
                  </span>
                  {!bankLocked && (
                    <>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "1px 4px", fontSize: FS.s9 }}
                        onClick={() => setBankForm({ ...b, _idx: i })}
                      >
                        <Settings size={I.s} />
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: "1px 4px", fontSize: FS.s9 }}
                        onClick={() => delBk(i)}
                      >
                        <Trash2 size={I.s} />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {bankForm && (
                <div
                  style={{
                    borderTop: "1px solid var(--br)",
                    marginTop: 8,
                    paddingTop: 8,
                  }}
                >
                  <div className="field-row">
                    <div className="field">
                      <label>
                        {_t("settings.label.accountName", {
                          fallback: "Account Name",
                        })}
                      </label>
                      <input autoComplete="off" /* 207 §18 */
                        className="field-input no-contact-autofill"
                        value={bankForm.name || ""}
                        onChange={(e) =>
                          setBankForm((p: any) => ({ ...p, name: e.target.value }))
                        }
                        placeholder={_t("settings.placeholder.accountName", {
                          fallback: "e.g. Main Account",
                        })}
                      />
                    </div>
                    <div className="field">
                      <label>
                        {_t("settings.label.bankName", {
                          fallback: "Bank Name",
                        })}
                      </label>
                      <input autoComplete="off" /* 207 §18 */
                        className="field-input no-contact-autofill"
                        value={bankForm.bankName || ""}
                        onChange={(e) =>
                          setBankForm((p: any) => ({
                            ...p,
                            bankName: e.target.value,
                          }))
                        }
                        placeholder={_t("settings.placeholder.bankName", {
                          fallback: "e.g. ING Belgium",
                        })}
                      />
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label>
                        {_t("settings.label.iban", { fallback: "IBAN *" })}
                      </label>
                      <input
                        className="field-input"
                        value={bankForm.iban || ""}
                        onChange={(e) =>
                          setBankForm((p: any) => ({ ...p, iban: e.target.value }))
                        }
                        placeholder="BE00 0000 0000 0000"
                        style={{ fontFamily: "'JetBrains Mono',monospace" }}
                      />
                    </div>
                    <div className="field">
                      <label>
                        {_t("settings.label.bic", { fallback: "BIC" })}
                      </label>
                      <input
                        className="field-input"
                        value={bankForm.bic || ""}
                        onChange={(e) =>
                          setBankForm((p: any) => ({ ...p, bic: e.target.value }))
                        }
                        style={{ fontFamily: "'JetBrains Mono',monospace" }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                    <button
                      className="btn btn-primary"
                      style={{ padding: "3px 10px", fontSize: FS.s10 }}
                      onClick={saveBk}
                    >
                      {_t("settings.btn.save", { fallback: "Save" })}
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: "3px 10px", fontSize: FS.s10 }}
                      onClick={() => setBankForm(null)}
                    >
                      {_t("settings.btn.cancel", { fallback: "Cancel" })}
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
            {/* Phase 3 C4 — Folder Templates panel (Integrations). Phase 8 F1 — wrapped
  in sh4+cls; the panel's internal h3 was removed so the chevron header
  is the only visible heading.
  Phase auth-c2 Stage 3 — gate is settings.integrations.folderTpl (panel
  visibility); the underlying writes split between
  lateral.clientFolderTemplate.edit and lateral.caseFolderTemplate.edit,
  enforced via compare-and-drop in /firm-settings. */}
            {sg("settings.integrations.folderTpl") && (
            <div
              className={`detail-card settings-pane${cls("folderTpl")}`}
              style={{ order: 42 }}
            >
              {sh4(
                "folderTpl",
                _t("settings.folder_templates.title", {
                  fallback: "Folder Templates",
                }),
              )}
              <FolderTemplatesPanel
                firmSettings={firmSettings}
                onUpdated={(r: any) => {
                  // Item 1 (413 fix): the panel's own field-PUT already
                  // persisted; refresh LOCAL state only — do NOT re-PUT the
                  // whole row (that echoed back the ~1 MB proformaTemplate blob
                  // and exceeded nginx's 1 MB body limit → 413).
                  if (applyFirmSettings) applyFirmSettings(r);
                  else if (saveFirmSettings) saveFirmSettings(r).catch(() => {});
                }}
              />
            </div>
            )}
            {sg("settings.billing.invNum") && (
            <div
              className={`detail-card settings-pane${cls("invNum")}`}
              style={{ order: 63 }}
            >
              {sh4(
                "invNum",
                _t("settings.panel.invoiceNumbering", {
                  fallback: "Invoice Numbering",
                }),
              )}
              <div className="field-row">
                <div className="field">
                  <label>
                    {_t("settings.label.proformaPrefix", {
                      fallback: "Pro Forma Prefix",
                    })}
                  </label>
                  <input
                    className="field-input"
                    style={{
                      width: 80,
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                    value={pfx}
                    onChange={(e) =>
                      updInv({ proformaPrefix: e.target.value })
                    }
                  />
                </div>
                {/* Phase 35 (C1 / D1) — the "Formal Prefix" input is removed:
                    the formal-invoice type is dropped (pro formas only), so its
                    counter/prefix are dead.
                    179 C.9 — the "Starting Number" field and the preview line
                    STOOD HERE (the allocator never read either); the
                    `settings.label.startingNumber` rows stay in the seed. */}
              </div>
              {/* Phase 35 (C1 / L2) — opt-in manual pro-forma-number override.
                  Default off → the server atomic allocator owns the number;
                  on → the create modal exposes an editable number field. */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: FS.s11,
                  cursor: "pointer",
                  marginTop: 10,
                }}
              >
                <input
                  type="checkbox"
                  checked={allowManualNum}
                  onChange={() =>
                    updInv({ proformaAllowManualNumber: !allowManualNum })
                  }
                />
                <span>
                  {_t("settings.label.allowManualProformaNumber", {
                    fallback:
                      "Allow manual override of the pro forma number",
                  })}
                </span>
              </label>
              <p style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 6 }}>
                {_t("settings.label.allowManualProformaNumber.help", {
                  fallback:
                    "When off, every pro forma is numbered automatically by the server. When on, the number can be edited in the create dialog (use with care — duplicate numbers are possible).",
                })}
              </p>
            </div>
            )}
            {sg("settings.billing.invTerms") && (
            <div
              className={`detail-card settings-pane${cls("invTerms")}`}
              style={{ order: 64 }}
            >
              {sh4(
                "invTerms",
                _t("settings.panel.paymentTerms", {
                  fallback: "Payment Terms",
                }),
              )}
              <div className="field-row">
                <div className="field">
                  <label>
                    {_t("settings.label.dueDays", { fallback: "Due Days" })}
                  </label>
                  <input
                    className="field-input"
                    type="number"
                    min={0}
                    style={{ width: 80 }}
                    value={dueDays}
                    onChange={(e) =>
                      updInv({ dueDays: Math.max(0, +e.target.value || 30) })
                    }
                  />
                </div>
                <div className="field">
                  <label>
                    {_t("settings.label.defaultVat", {
                      fallback: "Default VAT %",
                    })}
                  </label>
                  <input
                    className="field-input"
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    style={{ width: 80 }}
                    value={vatRate}
                    onChange={(e) =>
                      updInv({ vatRate: +e.target.value || 21 })
                    }
                  />
                </div>
                <div className="field">
                  <label>
                    {_t("settings.label.defaultCostsRatio", {
                      fallback: "Default Costs Ratio % (for new cases)",
                    })}
                  </label>
                  <input
                    className="field-input"
                    type="number"
                    min={0}
                    max={100}
                    style={{ width: 80 }}
                    value={costsRatio}
                    onChange={(e) =>
                      updInv({ costsRatio: +e.target.value || 19 })
                    }
                  />
                  <p
                    style={{
                      fontSize: FS.s10,
                      color: "var(--tm)",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {_t("settings.help.defaultCostsRatio", {
                      fallback:
                        "This is the default value for new cases. Each case has its own costs ratio that can be adjusted in the case edit form.",
                    })}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 6 }}>
                {_t("settings.help.paymentTerms", {
                  fallback:
                    "Default values for new invoices. Due days = number of days from invoice date until payment is due.",
                })}
              </p>
            </div>
            )}
            {sg("settings.billing.invGC") && (
            <div
              className={`detail-card settings-pane${cls("invGC")}`}
              style={{ order: 65 }}
            >
              {sh4(
                "invGC",
                _t("settings.panel.generalConditions", {
                  fallback: "General Conditions",
                }),
              )}
              <p
                style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 8 }}
              >
                {_t("settings.help.generalConditions", {
                  fallback:
                    "Paste or type general conditions text for each language. These appear on invoice footers.",
                })}
              </p>
              {gcLangs.map((lang) => (
                <div key={lang} className="field">
                  <label>
                    {_t(
                      "settings.gc." +
                        ({
                          nl: "dutch",
                          fr: "french",
                          en: "english",
                          de: "german",
                        }[lang] || lang),
                      { fallback: gcLabels[lang] },
                    )}
                  </label>
                  <textarea
                    className="field-input"
                    rows={3}
                    style={{ resize: "vertical", fontSize: FS.s10_5 }}
                    value={gc[lang] || ""}
                    onChange={(e) => {
                      const u = { ...gc, [lang]: e.target.value };
                      updInv({ generalConditions: u });
                    }}
                    placeholder={_t("settings.gc.placeholder", {
                      lang: gcLabels[lang],
                      fallback: `General conditions in ${gcLabels[lang]}…`,
                    })}
                  />
                </div>
              ))}
              {/* Phase 40 (D10) — trilingual pro-forma draft-email body. */}
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: "1px solid var(--br)",
                }}
              >
                <label style={{ fontSize: FS.s11, fontWeight: 600 }}>
                  {_t("settings.panel.proformaEmail", {
                    fallback: "Pro-forma e-mail (draft body)",
                  })}
                </label>
                <p
                  style={{
                    fontSize: FS.s10,
                    color: "var(--tm)",
                    margin: "4px 0 8px",
                  }}
                >
                  {_t("settings.help.proformaEmail", {
                    fallback:
                      'Draft body for the "Send to client" Outlook e-mail, per language (picked by the client’s language; empty → default text). Placeholders: {clientName} {invoiceNumber} {invoiceDate} {dueDate} {totalToPay} {subtotalExVat} {vatAmount} {paymentReference} {paymentConditions} {firmName} (and the other invoice scalar fields).',
                  })}
                </p>
                {["nl", "fr", "en"].map((lang) => (
                  <div key={lang} className="field">
                    <label>
                      {_t(
                        "settings.gc." +
                          { nl: "dutch", fr: "french", en: "english" }[lang],
                        { fallback: gcLabels[lang] },
                      )}
                    </label>
                    <textarea
                      className="field-input"
                      rows={3}
                      style={{ resize: "vertical", fontSize: FS.s10_5 }}
                      value={pet[lang] || ""}
                      onChange={(e) => {
                        const u = { ...pet, [lang]: e.target.value };
                        updInv({ proformaEmailTemplate: u });
                      }}
                      placeholder={_t("settings.proformaEmail.placeholder", {
                        lang: gcLabels[lang],
                        fallback: `Pro-forma e-mail body in ${gcLabels[lang]}…`,
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}
            {sg("settings.billing.invLogo") && (
            <div
              className={`detail-card settings-pane${cls("invLogo")}`}
              style={{ order: 66 }}
            >
              {sh4(
                "invLogo",
                _t("settings.panel.invoiceLogo", {
                  fallback: "Invoice Logo",
                }),
              )}
              <p
                style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 8 }}
              >
                {_t("settings.help.invoiceLogo", {
                  fallback:
                    "Upload a logo (PNG/JPG, max 500KB) to appear on invoice headers.",
                })}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {invLogoPreview && (
                  <img
                    src={invLogoPreview}
                    alt="Logo"
                    style={{
                      maxHeight: 40,
                      maxWidth: 160,
                      objectFit: "contain",
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r6,
                      padding: 4,
                    }}
                  />
                )}
                <label
                  className="btn btn-primary"
                  style={{
                    padding: "3px 10px",
                    fontSize: FS.s10,
                    cursor: "pointer",
                  }}
                >
                  <Upload size={10} style={{ marginRight: 4 }} />
                  {_t("settings.btn.upload", { fallback: "Upload" })}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleLogo}
                  />
                </label>
                {invLogoPreview && (
                  <button
                    className="btn btn-ghost"
                    style={{ padding: "3px 10px", fontSize: FS.s10 }}
                    onClick={() => {
                      updInv({ logo: null });
                    }}
                  >
                    {_t("settings.btn.remove", { fallback: "Remove" })}
                  </button>
                )}
              </div>
            </div>
            )}
            {sg("settings.billing.invPayTpl") && (
            <div
              className={`detail-card settings-pane${cls("invPayTpl")}`}
              style={{ order: 67 }}
            >
              {sh4(
                "invPayTpl",
                /* 170 §3d — the panel is named for what is left in it. Its
                   section key stays `invPayTpl`: renaming a key would orphan
                   every grant row that names it. */
                _t("settings.panel.particularConditionsTpl", {
                  fallback: "Particular Conditions Template",
                }),
              )}
              {/* 170 §3d [MEASURED] — THE PAYMENT-CONDITIONS TEXTAREA IS GONE,
                  and it edited a column nothing has ever read. `paymentTemplate`
                  (schema.prisma:1588) had exactly two readers in the tree, both
                  in this file: this textarea, and the line that fed it back into
                  it. Not the pro-forma modal, not docGen.js, not any route. A
                  control that writes to a dead column teaches a firm that it has
                  configured something.

                  THE PANE STAYS, because the field below it is alive and shares
                  its section key: `firmSettings.js:142` maps
                  `particularConditionsTemplate` to `settings.billing.invPayTpl`
                  too, so removing the key would take a live editor's gate with
                  it. The COLUMN also stays — a schema drop is its own unit,
                  after a prod check that it is empty (the brief's own bound). */}
              <p style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 8 }}>
                {_t("settings.help.particularTpl2", {
                  fallback:
                    "Pro forma “Particular conditions” template, per language (prefilled in the create dialog, editable there). Placeholders:",
                })}{" "}
                <code style={{ color: "var(--ac)" }}>{"{referenceRate}"}</code>,{" "}
                <code style={{ color: "var(--ac)" }}>{"{successFeeRate}"}</code>,{" "}
                <code style={{ color: "var(--ac)" }}>{"{costsRatio}"}</code>.
              </p>
              {/* 170 §3c [RULED 2026-09-08, Laurent] — PER LANGUAGE. It was one
                  textarea holding one sentence for every language, on a document
                  whose language is the client's. A legacy plain string still
                  edits correctly: the `typeof` guard reads it into all three
                  boxes, and the first save writes the map back. */}
              {(() => {
                const raw = inv.particularConditionsTemplate;
                const pcMap: Record<string, string> =
                  typeof raw === "string"
                    ? { fr: raw, en: raw, nl: raw }
                    : (raw && typeof raw === "object" ? raw : {});
                return ["fr", "en", "nl"].map((lang) => (
                  <div key={lang} className="field" style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: FS.s9_5, color: "var(--ts)" }}>{lang.toUpperCase()}</label>
                    <textarea
                      className="field-input"
                      rows={3}
                      style={{ resize: "vertical", fontSize: FS.s10_5 }}
                      placeholder={_t("settings.label.particularTpl_ph2", {
                        fallback:
                          "Reference hourly rate: {referenceRate} EUR/h. Fixed costs: {costsRatio}%. Success fee: {successFeeRate}%, provisions deducted.",
                      })}
                      value={pcMap[lang] || ""}
                      onChange={(e) =>
                        updInv({ particularConditionsTemplate: { ...pcMap, [lang]: e.target.value } })
                      }
                    />
                  </div>
                ));
              })()}
            </div>
            )}
            {/* 209 §14 [RULED 19] — THE APPROVAL SWITCH'S PANE (« Require Pro Forma
                Approval », `settings.billing.invPF`) STOOD HERE: approval is a gate
                before sending for every firm, so there is nothing to switch. */}
            {sg("settings.billing.invTpl") && (
            <div
              className={`detail-card settings-pane${cls("invTpl")}`}
              style={{ order: 69 }}
            >
              {sh4(
                "invTpl",
                _t("settings.panel.proFormaTemplate", {
                  fallback: "Pro Forma Template",
                }),
              )}
              {(() => {
                /* 170 §3f [MEASURED] — THE MARKERS COME FROM THE REGISTRY,
                   and this list is deleted rather than corrected. It was an
                   HTML-era hand-copy carrying FIVE markers the resolver does not
                   emit — {subtotalExVAT} (wrong case; it emits subtotalExVat),
                   {firmLogo}, {bankName}, {bankBankName}, {timeEntriesTable} —
                   so a firm following it built a template with blanks in it.

                   `docGenVariables.js` INVOICE_SCALARS is the real registry, it
                   is already served at GET /api/doc-templates/variables, and
                   `DocTemplatesPanel` has always read it from there. Two of the
                   three consumers were copies; now there are none, so a fourth
                   cannot start. The list is fetched once, above, and handed to
                   the XML editor too. */
                /* 170 §3e [MEASURED] — THE SECOND PRO-FORMA TEMPLATE IS GONE.
                   This panel held three things and only one of them was alive.

                   "Upload .docx" wrote a base64 blob to
                   `FirmSettings.proformaTemplate` (schema.prisma:1592) and
                   NOTHING READ IT. Across server/src it appears only in the
                   settings allow-list (firmSettings.js:42), the label map
                   (:140) and packFormat.js:190 — never in a render path. The
                   PDF is merged from `DocumentTemplate` + variants instead
                   (invoices.js:1509-1525, resolved BY KIND).

                   "Download sample template" built a .docx in the browser that
                   emitted `{subtotalExVAT}` (wrong case — the resolver emits
                   subtotalExVat) and `{timeEntriesTable}` (not a marker at
                   all). Anyone following it built a broken template, and 300
                   lines of docx-construction existed to hand it to them.

                   The XML Peppol editor below is alive (`proformaHtmlTemplate`,
                   read at invoices.js:1051 and :1131) and stays.

                   The COLUMN stays too — a schema drop is its own unit.

                   What replaces the dead upload is the REAL one: the
                   `DocumentTemplate` variants the PDF path actually reads, one
                   slot per language, mounted from `DocTemplatesPanel`'s own row
                   rather than forked. So the panel that says "Pro Forma
                   Template" is now the place that holds them. */
                return (
                  <>
                    <p
                      style={{
                        fontSize: FS.s10,
                        color: "var(--tm)",
                        margin: "4px 0 10px",
                        lineHeight: 1.5,
                      }}
                    >
                      {_t("settings.proformaTpl.intro2", {
                        fallback:
                          "Upload the Word template for each language, and customise the Peppol XML body below. Placeholders in curly braces are replaced with invoice data.",
                      })}
                    </p>
                    {/* 170 §3e — the real per-language slots, mounted from
                        DocTemplatesPanel's own row rather than forked. */}
                    {pfTpls.length === 0 ? (
                      <p style={{ fontSize: FS.s10, color: "var(--am)", margin: "0 0 10px" }}>
                        {_t("settings.proformaTpl.none", {
                          fallback:
                            "No pro-forma template is configured yet — create one in Document Templates (code kind “invoice”).",
                        })}
                      </p>
                    ) : (
                      pfTpls.map((tpl) => (
                        <div key={tpl.id} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: FS.s10, fontWeight: 600, color: "var(--tp)", marginBottom: 6 }}>
                            {String(tpl.name || tpl.code)} · {String(tpl.kind)}
                          </div>
                          <DocTemplateVariantSlots
                            tpl={tpl}
                            canEdit
                            busy={pfBusy}
                            onUpload={pfUpload}
                            onRemove={pfRemove}
                            onDownload={pfDownload}
                            t={_t}
                          />
                        </div>
                      ))
                    )}
                    {/* ── 170 §3b [RULED 2026-09-08, Laurent] — THE FEE LABELS,
                        IN SETTINGS. "No settings like that do not belong in a
                        5000 items translations table, but in settings."

                        They live in the Pro Forma Template pane rather than in a
                        pane of their own on purpose: a new section key would
                        have to be registered, granted, and would change
                        `recordings/63-api-snapshot`, which carries the section
                        catalog in three languages — a re-record to add a text
                        box. The wording of the pro-forma document belongs beside
                        the pro-forma document's template either way.

                        Empty means "say nothing new": docGen falls back to the
                        dictionary and then to the in-code English, so a firm
                        that never opens these keeps exactly the wording it has
                        today. That is why §3b does not delete the Translation
                        rows. */}
                    <div style={{ borderTop: "1px solid var(--br)", marginTop: 12, paddingTop: 10 }}>
                      <div style={{ fontSize: FS.s10, fontWeight: 600, color: "var(--tp)", marginBottom: 6 }}>
                        {_t("settings.feeLabels.title", { fallback: "Fee line wording" })}
                      </div>
                      <p style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 8 }}>
                        {_t("settings.feeLabels.help", {
                          fallback:
                            "The text of the fees line on a pro forma, per language. Leave a box empty to keep the standard wording.",
                        })}
                      </p>
                      {([
                        ["proformaFeesServicesLabel", _t("settings.feeLabels.services", { fallback: "Fees for professional services" })],
                        ["proformaFeesRetainerLabel", _t("settings.feeLabels.retainer", { fallback: "Retainer (provision)" })],
                        ["proformaFeesCreditLabel", _t("settings.feeLabels.credit", { fallback: "Credit note on {number}" })],
                      ] as [string, string][]).map(([col, label]) => {
                        const map = (inv[col] && typeof inv[col] === "object" ? inv[col] : {}) as Record<string, string>;
                        return (
                          <div key={col} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: FS.s9_5, color: "var(--ts)", marginBottom: 3 }}>{label}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                              {["fr", "en", "nl"].map((lang) => (
                                <input
                                  key={lang}
                                  className="field-input"
                                  style={{ fontSize: FS.s10_5 }}
                                  placeholder={lang.toUpperCase()}
                                  value={map[lang] || ""}
                                  onChange={(e) => {
                                    updInv({ [col]: { ...map, [lang]: e.target.value } });
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <ProformaXmlTemplateEditor
                      value={inv.proformaHtmlTemplate || ""}
                      onSave={(xml) => updInv({ proformaHtmlTemplate: xml })}
                      toast={toast}
                      // 170 §3f — one fetch, one list, handed to the second consumer.
                      markers={invoiceMarkers.map((m) => m.tag)}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: FS.s10,
                          fontWeight: 600,
                          color: "var(--tp)",
                          marginBottom: 6,
                        }}
                      >
                        {_t("settings.proformaTpl.placeholders", {
                          fallback: "Available Placeholders",
                        })}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: "2px 10px",
                          fontSize: FS.s10,
                        }}
                      >
                        {invoiceMarkers.map((m) => (
                          <div key={m.tag} style={{ display: "contents" }}>
                            <span
                              style={{
                                fontFamily: "'JetBrains Mono',monospace",
                                color: "var(--ac)",
                                fontSize: FS.s9,
                              }}
                            >
                              {`{${m.tag}}`}
                            </span>
                            <span style={{ color: "var(--tm)" }}>{m.desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            )}
          </>
        );
}

function AppearanceFontsPanel({
  cls,
  sh4,
  theme,
  textSize,
  setTextSize,
  autoNightSwitch,
  setAutoNightSwitch,
  dayStart,
  setDayStart,
  nightStart,
  setNightStart,
  themes,
  appFont,
  setAppFont,
  toast,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  const _i18n = useTranslation();
  // Derived from APP_FONTS rather than typed here, so the list a firm is
  // offered cannot drift from the list this box actually has a face for.
  // Inter, Roboto and Source Sans Pro left when the fonts stopped coming
  // from Google — nothing was self-hosted for them and nothing offers
  // them now. "Nunito Sans" is a proper noun and stays untranslated, as
  // the three dropped families did.
  const FONT_LABELS: Record<string, string> = {
    [DEFAULT_APP_FONT]: _i18n.t("settings.font.dmSans", {
      fallback: "DM Sans (default)",
    }),
    [SYSTEM_FONT]: _i18n.t("settings.font.systemDefault", {
      fallback: "System default",
    }),
  };
  const FONT_OPTS = APP_FONTS.map((v) => [v, FONT_LABELS[v] || v]);
  return (
    <div
      className={`detail-card settings-pane${cls("personalPrefs")}`}
      style={{ order: 11 }}
    >
      {sh4(
        "personalPrefs",
        _t("settings.panel.personalPrefs", {
          fallback: "Display & language",
        }),
      )}
      <p
        style={{
          fontSize: FS.s11,
          color: "var(--tm)",
          margin: "2px 0 10px",
          lineHeight: 1.5,
        }}
      >
        {_i18n.t("settings.desc.personalPrefs", {
          fallback:
            "Personal display preferences. Each user has their own.",
        })}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".4px",
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {_i18n.t("settings.subhead.textSize", {
              fallback: "Text size",
            })}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              [
                "auto",
                _i18n.t("settings.textSize.auto", { fallback: "Auto" }),
              ],
              [
                "small",
                _i18n.t("settings.textSize.small", { fallback: "Small" }),
              ],
              [
                "medium",
                _i18n.t("settings.textSize.medium", {
                  fallback: "Medium",
                }),
              ],
              [
                "large",
                _i18n.t("settings.textSize.large", { fallback: "Large" }),
              ],
              [
                "xlarge",
                _i18n.t("settings.textSize.xlarge", {
                  fallback: "Extra Large",
                }),
              ],
            ].map(([k, l]) => {
              const on = textSize === k;
              return (
                <button
                  key={k}
                  type="button"
                  className="btn"
                  style={{
                    padding: "4px 10px",
                    fontSize: FS.s11,
                    borderRadius: RAD.r8,
                    border:
                      "1px solid " + (on ? "var(--ac)" : "var(--br)"),
                    background: on ? "var(--as)" : "var(--bc)",
                    color: on ? "var(--ac)" : "var(--ts)",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontFamily: "inherit",
                  }}
                  onClick={() => setTextSize(k)}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".4px",
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {_i18n.t("settings.subhead.font", { fallback: "Font" })}
          </div>
          {/* 154B §2 — the option's own fontFamily IS the information
              here: the list shows each face in itself. That is what
              `optionStyle` widened MenuSelect for (THE FOUR RULES 4 —
              widen the primitive, never bend the behaviour), and it
              paints ROWS only, exactly as an <option style> did. */}
          <MenuSelect
            className="field-input"
            value={appFont}
            onChange={(v) => setAppFont(v)}
            options={FONT_OPTS.map(
              ([v, l]) => [String(v), String(l)] as const,
            )}
            optionStyle={(v) => ({ fontFamily: appFontStack(v) })}
          />
          <div
            style={{
              marginTop: 6,
              padding: "5px 8px",
              borderRadius: RAD.r6,
              border: "1px solid var(--br)",
              background: "var(--bi)",
              fontFamily: appFontStack(appFont),
              fontSize: FS.s11,
              color: "var(--ts)",
            }}
          >
            {_i18n.t("settings.fontPreview", {
              fallback: "The quick brown fox jumps over the lazy dog",
            })}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".4px",
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {_i18n.t("settings.subhead.language", {
              fallback: "Language",
            })}
          </div>
          <MenuSelect
            className="field-input"
            value={_i18n.lang}
            onChange={(v) => (_i18n as any).setLang(v)}
            options={_i18n.available.map(
              (lc: any) => [String(lc), String(lc).toUpperCase()] as const,
            )}
          />
        </div>
      </div>
      <div
        style={{
          borderTop: "1px solid var(--br)",
          marginTop: 14,
          paddingTop: 12,
        }}
      >
        <div
          style={{
            fontSize: FS.s10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".4px",
            color: "var(--tm)",
            marginBottom: 8,
          }}
        >
          {_i18n.t("settings.subhead.dayNightAuto", {
            fallback: "Day / Night auto-switch",
          })}
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: FS.s11,
            cursor: "pointer",
            color: "var(--tp)",
          }}
        >
          <input
            type="checkbox"
            checked={!!autoNightSwitch}
            onChange={(e) => setAutoNightSwitch(e.target.checked)}
          />
          {_i18n.t("settings.dayNight.checkbox", {
            fallback:
              "Automatically switch between Day and Night themes by time of day",
          })}
        </label>
        {autoNightSwitch && (
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 8,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div className="field" style={{ margin: 0 }}>
              <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {_i18n.t("settings.label.dayStart", {
                  fallback: "Day starts at",
                })}
              </label>
              <TimeField
                value={dayStart || "07:00"}
                onChange={(v) => {
                  if (v && nightStart && v >= nightStart) {
                    toast(
                      _i18n.t("settings.toast.dayBeforeNight", {
                        fallback:
                          "Day start must be earlier than night start",
                      }),
                    );
                    return;
                  }
                  setDayStart(v);
                }}
                style={{ padding: "3px 6px", fontSize: FS.s11, width: 120 }}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {_i18n.t("settings.label.nightStart", {
                  fallback: "Night starts at",
                })}
              </label>
              <TimeField
                value={nightStart || "19:00"}
                onChange={(v) => {
                  if (v && dayStart && v <= dayStart) {
                    toast(
                      _i18n.t("settings.toast.nightAfterDay", {
                        fallback:
                          "Night start must be later than day start",
                      }),
                    );
                    return;
                  }
                  setNightStart(v);
                }}
                style={{ padding: "3px 6px", fontSize: FS.s11, width: 120 }}
              />
            </div>
          </div>
        )}
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 6,
            fontStyle: "italic",
            lineHeight: 1.4,
          }}
        >
          {autoNightSwitch
            ? _i18n.t("settings.dayNight.brusselsOn", {
                fallback:
                  "Boundaries use Brussels local time. Manual picks from the sidebar Day/Night/Custom toggle override for the current session only.",
              })
            : _i18n.t("settings.dayNight.off", {
                fallback:
                  "Off — sidebar Day/Night/Custom toggle drives theme selection.",
              })}
        </p>
      </div>
    </div>
  );
}

function I18nDataPanel({
  cls,
  sh4,
  toast,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  const i18n = useTranslation();
  // Phase auth-c2 Stage 3 — was admin-only; now gated by
  // settings.data.i18n (admin overlay still short-circuits inside
  // can()).
  const fileRef = useRef<any>(null);
  const [busy2, setBusy2] = useState(false);
  // Phase 2b — preview holds {file, diff} while the admin reviews the diff;
  // the same file is re-uploaded to /api/i18n/apply on confirm (stateless).
  const [preview, setPreview] = useState<any>(null);
  const download = () => {
    // 174 §1.2 — a BLOB response, which api() cannot carry, so the bare fetch
    // stays and spreads exactly what api() spreads: X-LexCRM, the conditional
    // Bearer from lx:token, and the device header. Both credentials, one
    // definition (api.ts authHeaders).
    fetch("/api/i18n/export", {
      credentials: "same-origin",
      headers: authHeaders(),
    })
      .then(async (r) => {
        /* 195 §D.2 [JUDGEMENT — advisor; the class 193 §B cured on the admin
           toggle ten lines away] — a refusal's REASON is read, not thrown away:
           the envelope's `{ error, code }` through the same srvErr seam api()
           uses (a blob fetch bypasses api(), so the seam is called here), the
           server's own sentence when no row translates it, the status last. */
        if (!r.ok) {
          const body: unknown = await r.json().catch(() => null);
          const env = body && typeof body === "object" ? (body as { error?: unknown }) : null;
          throw new Error(srvErrText(body) ?? (typeof env?.error === "string" ? env.error : "") ?? String(r.status));
        }
        return r.blob();
      })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `lexcrm-i18n-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e: unknown) => {
        const reason = e instanceof Error ? e.message : "";
        toast(
          "⚠ " +
            (reason ||
              _t("settings.toast.download_failed", {
                fallback: "Download failed",
              })),
        );
      });
  };
  const startPreview = async (file: any) => {
    setBusy2(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // 174 §1.2 — a MULTIPART body, which api() cannot carry either (it sets
      // Content-Type: application/json and stringifies). Bare fetch, the same
      // three headers, and no Content-Type so fetch sets the boundary.
      const r = await fetch("/api/i18n/preview", {
        method: "POST",
        credentials: "same-origin",
        headers: authHeaders(),
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "HTTP " + r.status);
      setPreview({ file, diff: j });
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message ||
            _t("settings.toast.preview_failed", {
              fallback: "Preview failed",
            })),
      );
    }
    setBusy2(false);
  };
  const confirmApply = async () => {
    if (!preview) return;
    setBusy2(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      // 174 §1.2 — multipart, as /i18n/preview above.
      const r = await fetch("/api/i18n/apply", {
        method: "POST",
        credentials: "same-origin",
        headers: authHeaders(),
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "HTTP " + r.status);
      const tA = j.translations?.applied ?? 0;
      const rU = j.roles?.updated ?? 0;
      const xU = j.taxonomies?.updated ?? 0;
      toast(
        _t("settings.toast.i18n_applied", {
          translations: tA,
          roles: rU,
          taxonomies: xU,
          fallback: `Applied · translations:${tA} · roles:${rU} · taxonomies:${xU}`,
        }),
      );
      setPreview(null);
      await i18n.reload();
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message ||
            _t("settings.toast.apply_failed", {
              fallback: "Apply failed",
            })),
      );
    }
    setBusy2(false);
  };
  const renderDiffSection = (title: any, counts: any, sampleEntries: any, renderRow: any) => {
    if (!counts) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <h4
          style={{
            margin: "0 0 6px",
            fontSize: FS.s12,
            fontWeight: 600,
            color: "var(--tp)",
          }}
        >
          {title}
        </h4>
        <div
          style={{
            display: "flex",
            gap: 12,
            fontSize: FS.s10_5,
            color: "var(--ts)",
            marginBottom: 6,
            flexWrap: "wrap",
          }}
        >
          {Object.entries(counts).map(([k, v]) => (
            <span key={k}>
              <b style={{ color: "var(--tp)" }}>{v as any}</b>{" "}
              {k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
        {sampleEntries && sampleEntries.length > 0 && (
          <ul
            style={{
              margin: 0,
              padding: "0 0 0 16px",
              fontSize: FS.s10_5,
              color: "var(--tm)",
              maxHeight: 140,
              overflowY: "auto",
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {sampleEntries.map((it: any, i: any) => (
              <li key={i} style={{ marginBottom: 2, lineHeight: 1.4 }}>
                {renderRow(it)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  const renderModal = () => {
    if (!preview) return null;
    const {
      translations: Tdiff,
      roles: Rdiff,
      taxonomies: Xdiff,
    } = preview.diff;
    const tSamples = Tdiff
      ? [
          ...(Tdiff.samples.added || []).map((s: any) => ({
            ...s,
            _t: "added",
          })),
          ...(Tdiff.samples.modified || []).map((s: any) => ({
            ...s,
            _t: "modified",
          })),
          ...(Tdiff.samples.deleted || []).map((s: any) => ({
            ...s,
            _t: "deleted",
          })),
        ]
      : [];
    const rSamples = Rdiff
      ? [
          ...(Rdiff.samples.modified || []).map((s: any) => ({
            ...s,
            _t: "modified",
          })),
          ...(Rdiff.samples.skipped_new || []).map((s: any) => ({
            ...s,
            _t: "skipped-new",
          })),
          ...(Rdiff.samples.skipped_missing || []).map((s: any) => ({
            ...s,
            _t: "skipped-missing",
          })),
        ]
      : [];
    const xSamples = Xdiff
      ? [
          ...(Xdiff.samples.modified || []).map((s: any) => ({
            ...s,
            _t: "modified",
          })),
          ...(Xdiff.samples.skipped_new || []).map((s: any) => ({
            ...s,
            _t: "skipped-new",
          })),
          ...(Xdiff.samples.skipped_missing || []).map((s: any) => ({
            ...s,
            _t: "skipped-missing",
          })),
        ]
      : [];
    return (
      <ModalLayout
        title={_t("settings.i18n.previewTitle", {
          fallback: "Preview changes",
        })}
        onClose={() => setPreview(null)}
        closeOnOverlay
        closeDisabled={busy2}
        maxWidth={700}
        maxHeight="90vh"
        shellStyle={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        rawBody
        footer={
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              padding: "10px 20px",
              borderTop: "1px solid var(--br)",
            }}
          >
            <button
              className="btn btn-ghost"
              disabled={busy2}
              onClick={() => setPreview(null)}
            >
              {_t("common.cancel", { fallback: "Cancel" })}
            </button>
            <button
              className="btn btn-primary"
              disabled={busy2}
              onClick={confirmApply}
            >
              {busy2
                ? _t("settings.i18n.applying", { fallback: "Applying…" })
                : _t("settings.i18n.confirm", {
                    fallback: "Confirm & Apply",
                  })}
            </button>
          </div>
        }
      >
          <div
            style={{ padding: "14px 20px", overflowY: "auto", flex: 1 }}
          >
            <p
              style={{
                fontSize: FS.s11,
                color: "var(--tm)",
                margin: "0 0 14px",
                lineHeight: 1.5,
              }}
            >
              {_t("settings.i18n.previewNote", {
                fallback:
                  "Translations are full-replace (rows in DB but missing from xlsx are deleted). Role and Taxonomy sheets are label-refinement only — new rows in xlsx are skipped (use the per-row editors), and rows missing from xlsx keep their existing labels.",
              })}
            </p>
            {renderDiffSection(
              _t("settings.i18n.preview.translations", { fallback: "Translations" }),
              Tdiff?.counts,
              tSamples,
              (s: any) =>
                s._t === "modified" ? (
                  <>
                    <span style={{ color: "var(--ac)" }}>[{s._t}]</span>{" "}
                    {s.key}/{s.lang}:{" "}
                    {String(s.before || "").slice(0, 40)} →{" "}
                    {String(s.after || "").slice(0, 40)}
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        color:
                          s._t === "added"
                            ? "var(--gn)"
                            : s._t === "deleted"
                              ? "var(--rd)"
                              : "var(--ac)",
                      }}
                    >
                      [{s._t}]
                    </span>{" "}
                    {s.key}/{s.lang}
                    {s.text ? " · " + String(s.text).slice(0, 50) : ""}
                  </>
                ),
            )}
            {renderDiffSection(
              _t("settings.i18n.preview.roles", {
                fallback: "Roles (label refinement only)",
              }),
              Rdiff?.counts,
              rSamples,
              (s: any) => (
                <>
                  <span
                    style={{
                      color:
                        s._t === "modified" ? "var(--ac)" : "var(--tm)",
                    }}
                  >
                    [{s._t}]
                  </span>{" "}
                  {s.name}
                  {s.changes
                    ? " — " + Object.keys(s.changes).join(", ")
                    : ""}
                </>
              ),
            )}
            {renderDiffSection(
              _t("settings.i18n.preview.taxonomies", {
                fallback: "Taxonomies (label refinement only)",
              }),
              Xdiff?.counts,
              xSamples,
              (s: any) => (
                <>
                  <span
                    style={{
                      color:
                        s._t === "modified" ? "var(--ac)" : "var(--tm)",
                    }}
                  >
                    [{s._t}]
                  </span>{" "}
                  {s.kind}/{s.code}
                  {s.changes
                    ? " — " + Object.keys(s.changes).join(", ")
                    : ""}
                </>
              ),
            )}
          </div>
      </ModalLayout>
    );
  };
  return (
    <>
      <div
        className={`detail-card settings-pane${cls("i18n")}`}
        style={{ order: 104 }}
      >
        {sh4(
          "i18n",
          _t("settings.panel.translations", {
            fallback: "Manage Translations",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 10px",
            lineHeight: 1.6,
          }}
        >
          {/* 188 §F.5 [glance correction, Laurent 2026-09-14] — the words match the
              route (server/src/routes/i18n.js): the export writes FOUR sheets
              (translations · roles · taxonomies · subtask_templates — "3-sheet"
              was stale since Phase 7), /preview diffs without writing, /apply is
              one transaction where the translations sheet is a full replace
              and the other three scopes take label changes only. The seeded
              rows are corrected by the one-shot
              seed_migration_188_translations_panel_words.js (a deploy seed
              cannot reword a row a box already holds). */}
          {_t("settings.desc.translations", {
            fallback:
              "Download the catalogue as an Excel file with four sheets — translations, roles, taxonomies, subtask templates — edit it, and upload it again. The upload is previewed first as a diff; nothing is written until you confirm. The translations sheet replaces the table in full (a row missing from the sheet is deleted; a new column is a new language); roles, taxonomies and subtask templates take label changes only — new or missing rows are left as they are.",
          })}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={download}>
            {_t("settings.btn.downloadXlsx", {
              fallback: "↓ Download XLSX",
            })}
          </button>
          <button
            className="btn btn-ghost"
            disabled={busy2}
            onClick={() => fileRef.current?.click()}
          >
            {busy2 && !preview
              ? _t("settings.btn.previewing", { fallback: "Previewing…" })
              : _t("settings.btn.uploadXlsx", {
                  fallback: "↑ Upload XLSX",
                })}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) startPreview(f);
              e.target.value = "";
            }}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 8,
            lineHeight: 1.5,
          }}
        >
          {/* 188 §F.5 — the seeded rows of this key carried a first sentence
              ("Upload replaces all translations.") the fallback never had and
              the description above now states precisely; the one-shot brings
              the rows back to these words. */}
          {_t("settings.translations.note", {
            fallback: "Languages currently loaded:",
          })}{" "}
          <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>
            {i18n.available.map((l: any) => l.toUpperCase()).join(", ") || "—"}
          </span>
          .
        </p>
        {/* Brief 45 §1, ruled 2026-08-07 — MAKE IT FINDABLE. An admin who
            does not know a row is editable does not edit it, and the
            `calls.blur.*` rows are the one family in the catalogue that
            documents ANOTHER COMPANY'S product: Apple and Microsoft rename
            their menus on their own schedule, and when they do, the fix is
            a row here rather than a deploy.
            Added to THIS panel and not to a new one. The xlsx round-trip
            above is already the translations surface; a second editor for
            one key family would be the sixth prepared-and-disconnected
            mechanism brief 35 §4 refuses. */}
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.translations.note_foreign_ui", {
            fallback:
              "Keys beginning calls.blur. describe Apple's and Microsoft's own menus (the background-blur help note in a call). Those menus change on their schedule, not ours — if a step no longer matches what a colleague sees, correct that row here. One row per step, so a renamed menu item is a one-line edit. The note shows the date it was last known to be right; edit that row too.",
          })}
        </p>
      </div>
      {renderModal()}
    </>
  );
}

function RatesAdminBlock({
  cls,
  sh4,
  _meAuth,
  co,
  sCo,
  currentUser,
  toast,
  sg,
  s,
  _isAdminHere,
  _meAuthIsAdmin,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  const me = co.find((c: { id: number }) => c.id === currentUser);
  // Phase auth-c2 Stage 3 — IIFE was admin-only; now it short-
  // circuits only when NONE of the 4 inner panel gates are held.
  // Each panel inside is then per-gate. Admin overlay short-
  // circuits the gates anyway.
  const _innerRates =
    sg("settings.rates.emailFormula") ||
    sg("settings.rates.whatsappFormula") ||
    sg("settings.rates.docFormula") ||
    sg("settings.rates.emailCo");
  // Simulated-identity admin flag drives the Administrators
  // panel — under "Viewing as" a non-admin it hides too, matching
  // what that role would see. Header simulator stays actor-based.
  const _innerAdmin = _isAdminHere;
  const [ebCfg, setEbCfg] = useState<any>(null);
  const [ebSaving, setEbSaving] = useState(false);
  // Phase 12 — document-billing rates (six Setting keys, separate from email).
  const [dbCfg, setDbCfg] = useState<any>(null);
  const [dbSaving, setDbSaving] = useState(false);
  // BRIEF 108 §1 — WhatsApp billing rates. SIX Setting keys, and two of
  // them have no email counterpart: a chat is dominated by one-word
  // replies and media placeholders, so the formula needs a floor below
  // which nothing is charged and a minimum at which a charged message
  // starts. Typed rather than `any` — it is new code (§CONSTRAINTS), and
  // the field names are the full words the Setting keys use.
  const [whatsappCfg, setWhatsappCfg] = useState<WhatsappRateForm | null>(null);
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  // §4 — the import card's destination toggle defaults to one side, and
  // that side is a firm decision rather than a rate. It rides this panel
  // because it is the same gate, the same save and the same subject; a
  // Setting with no screen behind it would be a value nobody can reach.
  const [whatsappDestination, setWhatsappDestination] =
    useState<WhatsappDestination>(asWhatsappDestination(undefined));
  useEffect(() => {
    let c = false;
    api<Record<string, unknown>>("/settings")
      .then((d) => {
        if (c) return;
        setWhatsappCfg(readWhatsappRates(d));
        setWhatsappDestination(asWhatsappDestination(d?.[WHATSAPP_DESTINATION_KEY]));
        setEbCfg({
          readingMin: d.emailReadingMinutes ?? 5,
          readingBlk: d.emailReadingBlockSize ?? 150,
          writingMin: d.emailWritingMinutes ?? 10,
          writingBlk: d.emailWritingBlockSize ?? 150,
          // Phase 33 (Change C) — Outlook category write-back colours.
          billedColor: d.emailBilledCategoryColor ?? "preset3",
          savedColor: d.emailSavedCategoryColor ?? "preset4",
          // 181 A.6 (ruling 5) — the third, purple by default.
          attachmentsColor: d.emailAttachmentsCategoryColor ?? "preset8",
        });
        setDbCfg({
          readingMin: d.docReadingMinutes ?? 5,
          readingBlk: d.docReadingBlockSize ?? 150,
          reviewMin: d.docReviewMinutes ?? 10,
          reviewBlk: d.docReviewBlockSize ?? 150,
          writingMin: d.docWritingMinutes ?? 25,
          writingBlk: d.docWritingBlockSize ?? 150,
        });
      })
      .catch(() => {
        if (!c) {
          setWhatsappCfg(readWhatsappRates({}));
          setEbCfg({
            readingMin: 5,
            readingBlk: 150,
            writingMin: 10,
            writingBlk: 150,
            billedColor: "preset3",
            savedColor: "preset4",
            attachmentsColor: "preset8",
          });
          setDbCfg({
            readingMin: 5,
            readingBlk: 150,
            reviewMin: 10,
            reviewBlk: 150,
            writingMin: 25,
            writingBlk: 150,
          });
        }
      });
    return () => {
      c = true;
    };
  }, []);
  const saveEb = async () => {
    if (!ebCfg) return;
    setEbSaving(true);
    try {
      await api("/settings", {
        method: "PUT",
        body: JSON.stringify({
          emailReadingMinutes: +ebCfg.readingMin,
          emailReadingBlockSize: +ebCfg.readingBlk,
          emailWritingMinutes: +ebCfg.writingMin,
          emailWritingBlockSize: +ebCfg.writingBlk,
          // Phase 33 (Change C) — Outlook category write-back colours.
          emailBilledCategoryColor: ebCfg.billedColor,
          emailSavedCategoryColor: ebCfg.savedColor,
          // 181 A.6 (ruling 5) — the third, read by m365.js's stamp and by the
          // e-mail row's purple dot (useOutlookCategoryColors).
          emailAttachmentsCategoryColor: ebCfg.attachmentsColor,
        }),
      });
      toast(
        _t("settings.toast.emailRatesSaved", {
          fallback: "Email billing settings saved",
        }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || _t("common.save_failed", { fallback: "Save failed" })));
    }
    setEbSaving(false);
  };
  const saveWhatsapp = async () => {
    if (!whatsappCfg) return;
    setWhatsappSaving(true);
    try {
      await api("/settings", {
        method: "PUT",
        body: JSON.stringify({
          ...whatsappRatePayload(whatsappCfg),
          [WHATSAPP_DESTINATION_KEY]: whatsappDestination,
        }),
      });
      toast(
        _t("settings.toast.whatsappRatesSaved", {
          fallback: "WhatsApp billing settings saved",
        }),
      );
    } catch (e: unknown) {
      // `unknown`, not `any` — new code in src/ carries explicit types
      // (§CONSTRAINTS), and the two sibling savers' `any` is the debt the
      // ratchet exists to stop growing.
      const message = e instanceof Error ? e.message : "";
      toast("⚠ " + (message || _t("common.save_failed", { fallback: "Save failed" })));
    }
    setWhatsappSaving(false);
  };
  const saveDb = async () => {
    if (!dbCfg) return;
    setDbSaving(true);
    try {
      await api("/settings", {
        method: "PUT",
        body: JSON.stringify({
          docReadingMinutes: +dbCfg.readingMin,
          docReadingBlockSize: +dbCfg.readingBlk,
          docReviewMinutes: +dbCfg.reviewMin,
          docReviewBlockSize: +dbCfg.reviewBlk,
          docWritingMinutes: +dbCfg.writingMin,
          docWritingBlockSize: +dbCfg.writingBlk,
        }),
      });
      toast(
        _t("settings.toast.docRatesSaved", {
          fallback: "Document billing rates saved",
        }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || _t("common.save_failed", { fallback: "Save failed" })));
    }
    setDbSaving(false);
  };
  const internals = co.filter(
    (c: any) => !c.coType || c.coType === "internal",
  );
  // Admin toggle plumbing. The admin-toggle tile only renders when the
  // logged-in actor is admin — uses the outer-scope _meAuth /
  // _meAuthIsAdmin (read from lx:authUser, never from lx:currentUser
  // which may hold a simulated identity).
  const [adminSaveCo, setAdminSaveCo] = useState<any>(null);
  const toggleAdmin = async (collab: any) => {
    if (collab.id === _meAuth?.id && collab.isAdmin) {
      toast(
        _t("settings.toast.cannotRemoveAdmin", {
          fallback: "⚠ You cannot remove admin from yourself",
        }),
      );
      return;
    }
    setAdminSaveCo(collab.id);
    try {
      // 174 §1.2 — a JSON PUT, so it goes through the door: BOTH credentials,
      // and api() supplies the /api prefix and the CSRF header.
      const updated = (await api.put(`/collaborators/${collab.id}`, {
        isAdmin: !collab.isAdmin,
      })) as { isAdmin?: boolean };
      s(
        SK.co,
        co.map((x: any) =>
          x.id === collab.id
            ? { ...x, isAdmin: updated.isAdmin ?? !collab.isAdmin }
            : x,
        ),
        sCo,
      );
      toast(
        updated.isAdmin
          ? _t("settings.toast.adminAdded", {
              name: pname(collab),
              fallback: `${pname(collab)} is now an admin`,
            })
          : _t("settings.toast.adminRemoved", {
              name: pname(collab),
              fallback: `${pname(collab)} is no longer an admin`,
            }),
      );
    } catch (e: unknown) {
      // 193 §B — the toggle says what the server said. The one refusal this
      // tile can earn by design is the two-admin floor (`collab.admin_floor`,
      // killSwitch.js) — an ASSEMBLED sentence ("…at least 2 … would leave 1")
      // that the api layer's srvErr seam keeps in the server's English because
      // no patterned row matches it (srvErr.ts's rule), so that code's own row
      // is read here, in the viewer's language; any other refusal reads its
      // text; the fixed "Toggle failed" only when there is no text at all. The
      // saver ten lines above always showed the text; this catch threw it away.
      const err = e as { message?: unknown; code?: unknown } | null;
      const message = typeof err?.message === "string" ? err.message : "";
      const text =
        err?.code === "collab.admin_floor"
          ? _t("srvErr.collab.admin_floor", { fallback: message })
          : message;
      toast(
        text
          ? "⚠ " + text
          : _t("settings.toast.toggleFailed", {
              fallback: "⚠ Toggle failed",
            }),
      );
    }
    setAdminSaveCo(null);
  };
  return (
    <>
      {sg("settings.rates.emailFormula") && (
      <div
        className={`detail-card settings-pane${cls("emailBilling")}`}
        style={{ order: 70 }}
      >
        {sh4(
          "emailBilling",
          _t("settings.panel.emailBilling", {
            fallback: "Email Billing — Formula",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 10px",
            lineHeight: 1.6,
          }}
        >
          {_t("settings.desc.emailBilling", {
            fallback:
              "Minutes billed per started block of N words, separately for received (reading) and sent (writing) emails.",
          })}
        </p>
        {!ebCfg ? (
          <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
            {_t("settings.label.loading", { fallback: "Loading…" })}
          </p>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 8,
              }}
            >
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.readingMinutes", {
                    fallback: "Reading — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={ebCfg.readingMin}
                  onChange={(e) =>
                    setEbCfg((p: any) => ({
                      ...p,
                      readingMin: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.readingWords", {
                    fallback: "Reading — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={ebCfg.readingBlk}
                  onChange={(e) =>
                    setEbCfg((p: any) => ({
                      ...p,
                      readingBlk: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.writingMinutes", {
                    fallback: "Writing — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={ebCfg.writingMin}
                  onChange={(e) =>
                    setEbCfg((p: any) => ({
                      ...p,
                      writingMin: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.writingWords", {
                    fallback: "Writing — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={ebCfg.writingBlk}
                  onChange={(e) =>
                    setEbCfg((p: any) => ({
                      ...p,
                      writingBlk: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            {/* Phase 33 (Change C) — Outlook category write-back colours. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 8,
                marginTop: 8,
              }}
            >
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.recordedColor", {
                    fallback: "Outlook “recorded” colour",
                  })}
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <OutlookColorSelect
                    value={ebCfg.billedColor}
                    onChange={(v: any) =>
                      setEbCfg((p: any) => ({ ...p, billedColor: v }))
                    }
                  />
                </div>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.savedColor", {
                    fallback: "Outlook “saved” colour",
                  })}
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <OutlookColorSelect
                    value={ebCfg.savedColor}
                    onChange={(v: any) =>
                      setEbCfg((p: any) => ({ ...p, savedColor: v }))
                    }
                  />
                </div>
              </div>
              {/* 181 A.6 (ruling 5) — the third category, "attachments". */}
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.attachmentsColor", {
                    fallback: "Outlook “attachments” colour",
                  })}
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <OutlookColorSelect
                    value={ebCfg.attachmentsColor}
                    onChange={(v: any) =>
                      setEbCfg((p: any) => ({ ...p, attachmentsColor: v }))
                    }
                  />
                </div>
              </div>
            </div>
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 6 }}>
              {/* 181 A.6 — a NEW key for the three-colour sentence: the seed
                  only inserts, so a changed string needs a new key (105 §1). */}
              {_t("settings.help.categoryColors3", {
                fallback:
                  "Colours LexCRM applies when it tags billed / saved emails, and emails whose attachments were saved, in your Outlook.",
              })}
            </p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 10 }}
              disabled={ebSaving}
              onClick={saveEb}
            >
              {ebSaving
                ? _t("settings.btn.saving", { fallback: "Saving…" })
                : _t("settings.btn.save", { fallback: "Save" })}
            </button>
          </>
        )}
      </div>
      )}
      {/* BRIEF 108 §1 — the WhatsApp formula, the sibling of the panel
          above: same card, same field grid, same save button, one gate
          of its own. SIX fields rather than four — the free-word floor
          and the per-message minimum are the two a chat needs and a
          letter does not. */}
      {sg("settings.rates.whatsappFormula") && (
      <div
        className={`detail-card settings-pane${cls("whatsappBilling")}`}
        style={{ order: 70 }}
      >
        {sh4(
          "whatsappBilling",
          _t("settings.panel.whatsappBilling", {
            fallback: "WhatsApp Billing — Formula",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 10px",
            lineHeight: 1.6,
          }}
        >
          {_t("settings.desc.whatsappBilling", {
            fallback:
              "Minutes billed per started block of N words, separately for received (reading) and sent (writing) messages. Messages at or below the free-word count cost nothing; anything above it costs at least the minimum.",
          })}
        </p>
        {!whatsappCfg ? (
          <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
            {_t("settings.label.loading", { fallback: "Loading…" })}
          </p>
        ) : (
          <>
            {/* 108 (b) — SIX fields, so the column count must divide
                six. The email sibling's `minmax(180px,1fr)` yields FIVE
                columns inside the 960px settings column
                (SettingsView.tsx:700) — a 5+1 orphan, and columns of
                ~172px, which is what wrapped the longest label onto two
                lines and dropped its input below the row.

                `260px` cannot reach four columns here: four would need
                4x260 + 3x8 = 1064px and the pane has ~934px inside its
                padding. So the count is 3, 2 or 1 at every width this
                screen can take — each of which divides six, so no field
                is ever orphaned — and the narrowest column is 260px
                against the 4-column ~229px the email panel already
                renders without wrapping. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
                gap: 8,
              }}
            >
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappFreeWordsPerMessage", {
                    fallback: "Free words/message",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={whatsappCfg.freeWords}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, freeWords: e.target.value },
                    )
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappMinimumPerMessage", {
                    fallback: "Minimum minutes/message",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={whatsappCfg.minimumMinutes}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, minimumMinutes: e.target.value },
                    )
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappReadingMinutes", {
                    fallback: "Reading — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={whatsappCfg.readingMinutes}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, readingMinutes: e.target.value },
                    )
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappReadingBlockSize", {
                    fallback: "Reading — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={whatsappCfg.readingBlockSize}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, readingBlockSize: e.target.value },
                    )
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappWritingMinutes", {
                    fallback: "Writing — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={whatsappCfg.writingMinutes}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, writingMinutes: e.target.value },
                    )
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.whatsappWritingBlockSize", {
                    fallback: "Writing — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={whatsappCfg.writingBlockSize}
                  onChange={(e) =>
                    setWhatsappCfg((p: WhatsappRateForm | null) =>
                      p && { ...p, writingBlockSize: e.target.value },
                    )
                  }
                />
              </div>
            </div>
            {/* §1 — the hint the brief asks for, under the two block-size
                fields. It states the counter-intuitive half: a block is
                a FLOOR as well as a rate, because a started block is
                charged whole. */}
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 6 }}>
              {_t("settings.help.whatsappBlockSize", {
                fallback:
                  "A started block is charged whole, so the block size is also a per-message minimum: at the same rate per word, a larger block makes short messages cost more and leaves long ones unchanged.",
              })}
            </p>
            {/* §4 — the import card's destination toggle starts on this
                side. Not a rate, so it sits below the rule and above the
                one Save the whole panel shares. */}
            <div
              className="field"
              style={{ margin: 0, marginTop: 12, maxWidth: 360 }}
            >
              <label>
                {_t("settings.label.whatsappDefaultDestination", {
                  fallback: "Default filing destination",
                })}
              </label>
              <MenuSelect
                className="field-input"
                value={whatsappDestination}
                onChange={(v) =>
                  setWhatsappDestination(asWhatsappDestination(v))
                }
                options={[
                  [
                    "case",
                    _t("settings.option.whatsappDestinationCase", {
                      fallback: "The case folder's correspondence subfolder",
                    }),
                  ],
                  [
                    "client",
                    _t("settings.option.whatsappDestinationClient", {
                      fallback: "The client folder's confidential subfolder",
                    }),
                  ],
                ]}
              />
            </div>
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 6 }}>
              {_t("settings.help.whatsappDestination", {
                fallback:
                  "Where an imported conversation's PDF and original file are saved. The import card can be switched to the other side for any one import.",
              })}
            </p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 10 }}
              disabled={whatsappSaving}
              onClick={saveWhatsapp}
            >
              {whatsappSaving
                ? _t("settings.btn.saving", { fallback: "Saving…" })
                : _t("settings.btn.save", { fallback: "Save" })}
            </button>
          </>
        )}
      </div>
      )}
      {sg("settings.rates.docFormula") && (
      <div
        className={`detail-card settings-pane${cls("docBilling")}`}
        style={{ order: 70 }}
      >
        {sh4(
          "docBilling",
          _t("settings.panel.docBilling", {
            fallback: "Document Billing — Formula",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 10px",
            lineHeight: 1.6,
          }}
        >
          {_t("settings.desc.docBilling", {
            fallback:
              "Minutes billed per started block of N words for case documents, separately for reading, reviewing and writing. Used by the Bill documents modal.",
          })}
        </p>
        {!dbCfg ? (
          <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
            {_t("settings.label.loading", { fallback: "Loading…" })}
          </p>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 8,
              }}
            >
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docReadingMinutes", {
                    fallback: "Reading — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={dbCfg.readingMin}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({
                      ...p,
                      readingMin: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docReadingWords", {
                    fallback: "Reading — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={dbCfg.readingBlk}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({
                      ...p,
                      readingBlk: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docReviewMinutes", {
                    fallback: "Review — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={dbCfg.reviewMin}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({ ...p, reviewMin: e.target.value }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docReviewWords", {
                    fallback: "Review — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={dbCfg.reviewBlk}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({ ...p, reviewBlk: e.target.value }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docWritingMinutes", {
                    fallback: "Writing — minutes/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={0}
                  value={dbCfg.writingMin}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({
                      ...p,
                      writingMin: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>
                  {_t("settings.label.docWritingWords", {
                    fallback: "Writing — words/block",
                  })}
                </label>
                <input
                  className="field-input"
                  type="number"
                  min={1}
                  value={dbCfg.writingBlk}
                  onChange={(e) =>
                    setDbCfg((p: any) => ({
                      ...p,
                      writingBlk: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 10 }}
              disabled={dbSaving}
              onClick={saveDb}
            >
              {dbSaving
                ? _t("settings.btn.saving", { fallback: "Saving…" })
                : _t("settings.btn.save", { fallback: "Save" })}
            </button>
          </>
        )}
      </div>
      )}
      {/* Phase 21 AUTH — the "Email Billing — Collaborators" panel was
          removed. Per-collaborator billing rights now live in the
          permission matrix (caseView.emails.billing /
          caseView.documents.billing role defaults + per-case grants),
          replacing the retired per-user emailBillingEnabled flag. */}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("adminCo")}`}
          style={{ order: 51 }}
        >
          {sh4(
            "adminCo",
            _t("settings.panel.administrators", {
              fallback: "Administrators",
            }),
          )}
          <p
            style={{
              fontSize: FS.s11_5,
              color: "var(--ts)",
              margin: "6px 0 10px",
              lineHeight: 1.6,
            }}
          >
            {_t("settings.desc.administrators", {
              fallback:
                'Admins can impersonate other users via the "Viewing as" switcher, edit the permissions matrix, and manage these settings. You cannot remove your own admin flag — ask another admin, or use a break-glass account.',
            })}
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            {internals.map((c: any) => {
              const isSelf = c.id === _meAuth?.id;
              const saving = adminSaveCo === c.id;
              return (
                <label
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: FS.s11,
                    padding: "4px 0",
                    borderBottom: "1px solid rgba(255,255,255,.04)",
                    cursor:
                      saving || (isSelf && c.isAdmin)
                        ? "not-allowed"
                        : "pointer",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!c.isAdmin}
                    disabled={saving || (isSelf && c.isAdmin)}
                    onChange={() => toggleAdmin(c)}
                  />
                  <span style={{ flex: 1 }}>
                    {pname(c)}
                    <span
                      style={{
                        color: "var(--tm)",
                        fontSize: FS.s10,
                        marginLeft: 6,
                      }}
                    >
                      {roleLabel(c.role)}
                    </span>
                    {isSelf && (
                      <span
                        style={{
                          color: "var(--ac)",
                          fontSize: FS.s9,
                          marginLeft: 6,
                          padding: "1px 5px",
                          borderRadius: RAD.r4,
                          background: "var(--as)",
                        }}
                      >
                        {_t("settings.tag.you", { fallback: "you" })}
                      </span>
                    )}
                    {c.isAdmin && (
                      <span
                        style={{
                          color: "var(--gn)",
                          fontSize: FS.s9,
                          marginLeft: 6,
                          padding: "1px 5px",
                          borderRadius: RAD.r4,
                          background: "var(--gs)",
                        }}
                      >
                        {_t("settings.tag.admin", { fallback: "admin" })}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function PwaInstallPanel({
  cls,
  sh4,
  toast,
  firmName,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  const [inst, setInst] = useState({
    available: !!window.__lxInstallPrompt,
    installed:
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches,
  });
  useEffect(() => {
    const a = () => setInst((s) => ({ ...s, available: true }));
    const b = () => setInst({ available: false, installed: true });
    window.addEventListener("lx:pwa-installable", a);
    window.addEventListener("lx:pwa-installed", b);
    return () => {
      window.removeEventListener("lx:pwa-installable", a);
      window.removeEventListener("lx:pwa-installed", b);
    };
  }, []);
  const install = async () => {
    const p = window.__lxInstallPrompt;
    if (!p) {
      toast(
        _t("settings.toast.installNA", {
          fallback:
            "Install prompt not available — use your browser menu",
        }),
      );
      return;
    }
    p.prompt();
    const r = await p.userChoice;
    if (r?.outcome === "accepted") {
      window.__lxInstallPrompt = null;
      setInst({ available: false, installed: true });
      toast(
        _t("settings.toast.appInstalled", { fallback: "App installed" }),
      );
    }
  };
  return (
    <div
      className={`detail-card settings-pane${cls("pwa")}`}
      style={{ order: 21 }}
    >
      {sh4(
        "pwa",
        _t("settings.panel.installApp", { fallback: "Install App" }),
      )}
      <p
        style={{
          fontSize: FS.s11_5,
          color: "var(--ts)",
          margin: "6px 0 10px",
          lineHeight: 1.6,
        }}
      >
        {_t("settings.desc.installApp_pre", { fallback: "Install" })}{" "}
        {firmName ||
          _t("settings.installApp.this_app", { fallback: "this app" })}{" "}
        {_t("settings.desc.installApp_post", {
          fallback:
            "as a desktop app — runs in its own window without the browser address bar.",
        })}
      </p>
      {inst.installed ? (
        <p style={{ fontSize: FS.s11, color: "var(--gn)" }}>
          {_t("settings.installApp.installed", {
            fallback: "✓ Installed — launch from your applications menu.",
          })}
        </p>
      ) : inst.available ? (
        <button className="btn btn-primary" onClick={install}>
          {_t("settings.installApp.installCta", {
            fallback: "↓ Install",
          })}{" "}
          {firmName ||
            _t("settings.installApp.app_noun", { fallback: "app" })}
        </button>
      ) : (
        <p
          style={{ fontSize: FS.s10_5, color: "var(--tm)", lineHeight: 1.5 }}
        >
          {/* Stage-3 C23 — accurate copy: the wiring (capture +
              lx:pwa-installable event + mount-time check) is correct;
              when no prompt exists it is because the browser never
              offered one — most often: already installed on this
              device, or an unsupported browser. */}
          {_t("settings.installApp.notOffered", {
            fallback:
              "The browser hasn't offered an install prompt here — usually because the app is already installed on this device, or this browser doesn't support installation. In Chrome/Edge you can still use the install icon in the address bar, or the ⋮ menu → “Install",
          })}{" "}
          {firmName ||
            _t("settings.installApp.app_noun", { fallback: "app" })}
          {_t("settings.installApp.helpFallbackEnd", { fallback: "…”." })}
        </p>
      )}
      <p
        style={{
          fontSize: FS.s10,
          color: "var(--tm)",
          lineHeight: 1.5,
          marginTop: 8,
          fontStyle: "italic",
        }}
      >
        {_t("settings.installApp.lazyNote", {
          fallback:
            "The installed app's name and bar color follow the firm settings (Theme Manager → Window bar & title). Already-installed apps pick up such changes lazily — on a later launch, not instantly.",
        })}
      </p>
    </div>
  );
}

function BulkMailerPanel({
  cls,
  sh4,
  toast,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  // Phase 25 RESHAPE — collective HTML mailer config (FirmSettings +
  // BulkMailTemplate). Gated by the M365 integration panel key. Pacing for
  // the fired modes (ii/iii), the fixed firm footer, and the firm-authored
  // body templates. (Bcc batch size + secretariat@ are retired.)
  /* 189 A.1 — the panel's state, typed at its seam (the `any` it wore cost a
     warning on every read; the checkbox added two more). The two number fields
     hold what the input holds, a string, until the save coerces them. */
  interface BulkMailTunables { perWindow: number | string; windowMin: number | string; footer: string; includeUnknown: boolean }
  const [bm, setBm] = useState<BulkMailTunables | null>(null);
  const [bmBusy, setBmBusy] = useState(false);
  const [tpls, setTpls] = useState<any>(null);
  const [tBusy, setTBusy] = useState(false);
  const [newT, setNewT] = useState({ name: "", bodyHtml: "" });
  useEffect(() => {
    let c = false;
    api<{
      bulkIndividualPerWindow?: number;
      bulkIndividualWindowMs?: number;
      mailFooterHtml?: string;
      marketingIncludeUnknownConsent?: boolean;
    }>("/firm-settings")
      .then((d) => {
        if (c) return;
        setBm({
          perWindow: d.bulkIndividualPerWindow ?? 20,
          windowMin: Math.round((d.bulkIndividualWindowMs ?? 180000) / 60000),
          footer: d.mailFooterHtml || "",
          includeUnknown: d.marketingIncludeUnknownConsent === true,
        });
      })
      .catch(() => {
        if (!c) setBm({ perWindow: 20, windowMin: 3, footer: "", includeUnknown: false });
      });
    return () => { c = true; };
  }, []);
  const loadTpls = () =>
    api("/bulk-mail-templates").then((r) => setTpls(r || [])).catch(() => setTpls([]));
  useEffect(() => { loadTpls(); }, []); // eslint-disable-line
  const saveBm = async () => {
    if (!bm) return;
    setBmBusy(true);
    try {
      await api("/firm-settings", {
        method: "PUT",
        body: JSON.stringify({
          bulkIndividualPerWindow: Math.max(1, +bm.perWindow || 20),
          bulkIndividualWindowMs: Math.max(1, +bm.windowMin || 1) * 60000,
          mailFooterHtml: bm.footer.trim() || null,
          marketingIncludeUnknownConsent: bm.includeUnknown,
        }),
      });
      toast(_t("settings.btn.saved", { fallback: "Saved" }));
    } catch (e: any) {
      toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" })));
    }
    setBmBusy(false);
  };
  const patchTpl = (id: any, p: any) =>
    setTpls((arr: any) => arr.map((x: any) => (x.id === id ? { ...x, ...p } : x)));
  const saveTpl = async (tp: any) => {
    setTBusy(true);
    try {
      await api(`/bulk-mail-templates/${tp.id}`, {
        method: "PUT",
        body: JSON.stringify({ name: tp.name, bodyHtml: tp.bodyHtml, isActive: tp.isActive }),
      });
      toast(_t("settings.btn.saved", { fallback: "Saved" }));
      loadTpls();
    } catch (e: any) { toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" }))); }
    setTBusy(false);
  };
  const delTpl = async (id: any) => {
    setTBusy(true);
    try {
      await api(`/bulk-mail-templates/${id}`, { method: "DELETE" });
      loadTpls();
    } catch (e: any) { toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" }))); }
    setTBusy(false);
  };
  const addTpl = async () => {
    if (!newT.name.trim() || !newT.bodyHtml.trim()) return;
    setTBusy(true);
    try {
      await api("/bulk-mail-templates", {
        method: "POST",
        body: JSON.stringify({ name: newT.name.trim(), bodyHtml: newT.bodyHtml }),
      });
      setNewT({ name: "", bodyHtml: "" });
      loadTpls();
    } catch (e: any) { toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" }))); }
    setTBusy(false);
  };
  return (
    <div className={`detail-card settings-pane${cls("bulkMailer")}`} style={{ order: 42 }}>
      {sh4(
        "bulkMailer",
        _t("settings.panel.bulkMailer", { fallback: "Collective bulk-mailing" }),
      )}
      {!bm ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {_t("common.loading", { fallback: "Loading…" })}
        </p>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8 }}>
            <div className="field" style={{ margin: 0 }}>
              <label>{_t("settings.label.indivPerWindow", { fallback: "Fired modes — per window" })}</label>
              <input
                className="field-input"
                type="number"
                min={1}
                value={bm.perWindow}
                onChange={(e) => setBm((p: any) => ({ ...p, perWindow: e.target.value }))}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>{_t("settings.label.indivWindowMin", { fallback: "Fired modes — window (minutes)" })}</label>
              <input
                className="field-input"
                type="number"
                min={1}
                value={bm.windowMin}
                onChange={(e) => setBm((p: any) => ({ ...p, windowMin: e.target.value }))}
              />
            </div>
          </div>
          {/* 189 A.1 — the one consent setting. Default OFF: a person the firm
              never asked is left out of every campaign until somebody records
              a consent on their record (189 (7): campaigns only — this never
              touches correspondence). */}
          <label className="field" style={{ margin: "8px 0 0", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={bm.includeUnknown}
              onChange={(e) => { const v = e.target.checked; setBm((p) => (p ? { ...p, includeUnknown: v } : p)); }}
            />
            <span>{_t("settings.label.consentIncludeUnknown", { fallback: "Include people whose consent was never asked in campaigns" })}</span>
          </label>
          <div className="field" style={{ margin: "8px 0 0" }}>
            <label>{_t("settings.label.mailFooter", { fallback: "Fixed firm footer (HTML)" })}</label>
            <textarea
              className="field-input"
              rows={6}
              value={bm.footer}
              onChange={(e) => setBm((p: any) => ({ ...p, footer: e.target.value }))}
              style={{ width: "100%", resize: "vertical", fontFamily: "monospace", fontSize: FS.s11 }}
            />
          </div>
          <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "8px 0 0", lineHeight: 1.5 }}>
            {_t("settings.desc.bulkMailer", {
              fallback:
                "Fired-mode pacing (recipients per paced window) and the FIXED firm footer (HTML), always appended below the sender's name/email on individually-sent mails.",
            })}
          </p>
          <button className="btn btn-primary" style={{ marginTop: 10 }} disabled={bmBusy} onClick={saveBm}>
            {bmBusy
              ? _t("settings.btn.saving", { fallback: "Saving…" })
              : _t("settings.btn.save", { fallback: "Save" })}
          </button>

          {/* Body templates manager */}
          <div style={{ marginTop: 16, borderTop: "1px solid var(--br)", paddingTop: 12 }}>
            <div style={{ fontSize: FS.s12, fontWeight: 600, color: "var(--tp)", marginBottom: 6 }}>
              {_t("settings.label.bodyTemplates", { fallback: "Body templates" })}
            </div>
            {tpls == null ? (
              <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>{_t("common.loading", { fallback: "Loading…" })}</p>
            ) : (
              <>
                {tpls.map((tp: any) => (
                  <div key={tp.id} style={{ border: "1px solid var(--br)", borderRadius: RAD.r6, padding: 8, marginBottom: 8 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                      <input
                        className="field-input"
                        value={tp.name}
                        onChange={(e) => patchTpl(tp.id, { name: e.target.value })}
                        style={{ flex: 1 }}
                      />
                      <label style={{ fontSize: FS.s11, color: "var(--tm)", display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="checkbox"
                          checked={tp.isActive}
                          onChange={(e) => patchTpl(tp.id, { isActive: e.target.checked })}
                        />
                        {_t("settings.label.tplActive", { fallback: "Active" })}
                      </label>
                    </div>
                    <textarea
                      className="field-input"
                      rows={3}
                      value={tp.bodyHtml}
                      onChange={(e) => patchTpl(tp.id, { bodyHtml: e.target.value })}
                      style={{ width: "100%", resize: "vertical", fontFamily: "monospace", fontSize: FS.s11 }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button className="btn btn-primary" style={{ fontSize: FS.s11, padding: "2px 8px" }} disabled={tBusy} onClick={() => saveTpl(tp)}>
                        {_t("settings.btn.save", { fallback: "Save" })}
                      </button>
                      <button className="btn btn-ghost" style={{ fontSize: FS.s11, padding: "2px 8px" }} disabled={tBusy} onClick={() => delTpl(tp.id)}>
                        {_t("common.delete", { fallback: "Delete" })}
                      </button>
                    </div>
                  </div>
                ))}
                {/* Add new */}
                <div style={{ border: "1px dashed var(--br)", borderRadius: RAD.r6, padding: 8 }}>
                  <input autoComplete="off" /* 207 §18 */
                    className="field-input no-contact-autofill"
                    placeholder={_t("settings.ph.tplName", { fallback: "New template name" })}
                    value={newT.name}
                    onChange={(e) => setNewT((p) => ({ ...p, name: e.target.value }))}
                    style={{ width: "100%", marginBottom: 6 }}
                  />
                  <textarea
                    className="field-input"
                    rows={3}
                    placeholder={_t("settings.ph.tplBody", { fallback: "Body HTML (may contain {firstName}, {amountDue}, …)" })}
                    value={newT.bodyHtml}
                    onChange={(e) => setNewT((p) => ({ ...p, bodyHtml: e.target.value }))}
                    style={{ width: "100%", resize: "vertical", fontFamily: "monospace", fontSize: FS.s11 }}
                  />
                  <button className="btn btn-primary" style={{ fontSize: FS.s11, padding: "2px 8px", marginTop: 6 }} disabled={tBusy} onClick={addTpl}>
                    {_t("settings.btn.addTemplate", { fallback: "Add template" })}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function M365ConnectionPanel({
  cls,
  sh4,
  toast,
  firmName,
}: SettingsPanelProps) {
  const { t: _t } = useTranslation();
  type M365Status = {
    connected: boolean;
    email: string | null;
    mailboxConfigured: boolean;
    sharedMailbox: string | null;
  };
  const [m365, setM365] = useState<{ loading: boolean } & M365Status>({
    loading: true,
    connected: false,
    email: null,
    mailboxConfigured: false,
    sharedMailbox: null,
  });
  const [busy, setBusy] = useState(false);
  /* 213 §11.3 — A STATUS THAT COULD NOT BE READ IS NOT « NOT CONNECTED ». Asked the 210 §1 way (again, twice, a
     second apart, each failure on the console — as the case e-mail pane asks it, 212 §22); still failing, the panel
     says 212 §22's words, « État Microsoft 365 inconnu », with « Réessayer », in place of the status and of a
     « Connect » that would be a guess. src/lib/m365Status.ts stays as designed (126 §7). */
  const { entry: msRead, again: msAgain, ask: askMs } = useReadMark(PAGE_LISTS.m365Status);
  const msLists = usePageListContext(msRead);
  useEffect(() => {
    let c = false;
    void askMs("the Microsoft 365 status", () => oneRow(api<M365Status>("/auth/microsoft/status"))).then((rows) => {
      if (c) return;
      const d = rows ? (rows[0] as M365Status | null) : null;
      setM365(d ? { loading: false, ...d } : { loading: false, connected: false, email: null, mailboxConfigured: false, sharedMailbox: null });
    });
    return () => {
      c = true;
    };
  }, [msAgain, askMs]);
  const connect = async () => {
    setBusy(true);
    try {
      const d = await api<{ url?: string }>("/auth/microsoft/login");
      if (d?.url) window.location.href = d.url;
    } catch (e: any) {
      toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" })));
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    try {
      await api("/auth/microsoft/disconnect", { method: "POST" });
      setM365({
        loading: false,
        connected: false,
        email: null,
        mailboxConfigured: m365.mailboxConfigured,
        sharedMailbox: m365.sharedMailbox,
      });
      toast(
        _t("settings.toast.disconnected", { fallback: "Disconnected" }),
      );
    } catch (e: any) {
      toast("⚠ " + (e.message || _t("common.failed", { fallback: "Failed" })));
    }
    setBusy(false);
  };
  return (
    <div
      className={`detail-card settings-pane${cls("m365")}`}
      style={{ order: 41 }}
    >
      {sh4(
        "m365",
        _t("settings.panel.microsoft365", { fallback: "Microsoft 365" }),
      )}
      <p
        style={{
          fontSize: FS.s11_5,
          color: "var(--ts)",
          margin: "6px 0 10px",
          lineHeight: 1.6,
        }}
      >
        {_t("settings.desc.microsoft365_pre", {
          fallback: "Connect your Microsoft 365 account to sync",
        })}{" "}
        {firmName || _t("settings.panel.agenda", { fallback: "agenda" })}{" "}
        {_t("settings.desc.microsoft365_postWith", {
          fallback: "events to the shared Outlook mailbox calendar",
        })}
        {m365.sharedMailbox ? (
          <>
            {" "}
            (
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: FS.s10_5,
              }}
            >
              {m365.sharedMailbox}
            </span>
            )
          </>
        ) : null}
        .
      </p>
      {msRead.failure && (
        <ListLoadContext.Provider value={msLists}>
          <ListNotLoaded lists={[PAGE_LISTS.m365Status]} words={(_t as Translate)("caseEmails.m365_status_unknown", { fallback: "Microsoft 365 status unknown" })} />
        </ListLoadContext.Provider>
      )}
      {!msRead.failure && (m365.loading ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {_t("settings.status.checking", {
            fallback: "Checking status…",
          })}
        </p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              borderRadius: RAD.r6,
              background: "var(--bi)",
              marginBottom: 10,
              fontSize: FS.s11,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: RAD.pill,
                background: m365.connected ? "var(--gn)" : "var(--tm)",
              }}
            />
            <span style={{ color: "var(--tp)" }}>
              {m365.connected
                ? _t("settings.status.connected", {
                    fallback: "Connected",
                  })
                : _t("settings.status.notConnected", {
                    fallback: "Not connected",
                  })}
            </span>
            {m365.email && (
              <span
                style={{
                  color: "var(--tm)",
                  marginLeft: "auto",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: FS.s10,
                }}
              >
                {m365.email}
              </span>
            )}
          </div>
          {!m365.mailboxConfigured && (
            <p
              style={{
                fontSize: FS.s10_5,
                color: "var(--am)",
                marginBottom: 8,
                lineHeight: 1.5,
              }}
            >
              {_t("settings.warning.noSharedMailbox", {
                fallback:
                  "⚠ No shared mailbox configured. Set M365_SHARED_MAILBOX in the server environment. Sync is disabled until then.",
              })}
            </p>
          )}
          {m365.connected ? (
            <button
              className="btn btn-danger"
              disabled={busy}
              onClick={disconnect}
            >
              {_t("settings.btn.disconnect", { fallback: "Disconnect" })}
            </button>
          ) : (
            <button className="btn btn-primary" disabled={busy} onClick={connect}>
              {_t("settings.btn.connectM365", {
                fallback: "Connect to Microsoft 365",
              })}
            </button>
          )}
        </>
      ))}
    </div>
  );
}

/* ── 204 §A.3 [RULED — Laurent, 2026-09-22: "One firm-level main colour, taken
   from the Day theme, emitted once on the root as its own var, read by the
   wordmark and the documents."] — THE SWATCH, WHERE HE LOOKED FOR IT: under the
   firm's name in the Branding block, not in the theme editor's Accents group.
   It edits ONE value, the DAY theme's `--brand` — the value `--firm-brand`
   (App.tsx's theme effect) and every exported document's title derive from
   (`deriveDocumentPalette`, 203-glance §C.1) — through the theme route's own
   PATCH, exactly as ThemeManager's `saveEdit` writes a theme. That route
   REPLACES `vars` (themes.js: "Full replace … a merge would prevent removing
   overrides"), so the row's whole stored map travels with the one key changed;
   and its sanitiser DROPS a value that is not six-digit hex, silently — a
   half-typed `#4a7` sent as `--brand` would not refuse, it would delete the
   firm's colour — so nothing leaves this component but `#rrggbb`.
   THE CADENCE IS THE PANE'S OWN: the App-window-colour field beside this block
   writes a preference through `usePreferences`, which applies locally at once
   and PATCHes 500 ms after the last change, rolling back with a toast when the
   server refuses (its DEBOUNCE_MS). The same here — `<input type="color">`
   fires on every tick of a drag, and a PATCH per tick would publish a `config`
   row to the whole firm per tick; the live `setThemes` is what lets the
   sidebar's name follow the drag, the debounced PATCH is what lands it.
   GATED as the block it sits in (`_isAdminHere` at the mount, censused: the
   Branding block's own gate), and the server agrees — the Day theme is a basis
   row, which `PATCH /api/themes/:id` lets only an admin touch.
   Reads what it shows through the same derivation the effect uses, so the
   swatch and the wordmark cannot disagree; the label is the seeded « Main
   colour » (`theme.var.brand2`, 203-glance-2 §B) — that name belongs to this
   field now (204 §A.4 renames the editor's row). */
const FIRM_BRAND_DEBOUNCE_MS = 500;
const HEX6 = /^#[0-9a-fA-F]{6}$/;
type ThemeRowLike = { id: number; vars?: Record<string, string> | null };
function FirmMainColourField({
  themes,
  setThemes,
  firmSettings,
  toast,
  t,
}: {
  themes: ThemeRowLike[];
  setThemes: React.Dispatch<React.SetStateAction<ThemeRowLike[]>>;
  firmSettings: { dayThemeId?: number | null } | null | undefined;
  toast: (msg: string) => void;
  t: Translate;
}) {
  const resolved = deriveDocumentPalette(dayThemeVarsOf(themes, firmSettings)).brand;
  const dayId = firmSettings?.dayThemeId;
  const day = typeof dayId === "number" ? themes.find((x) => x.id === dayId) : undefined;
  // the hex field's in-progress text; null = show the resolved value
  const [draft, setDraft] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // one PATCH per burst of edits: the row's vars BEFORE the burst (the rollback),
  // the vars as the last edit left them (what is sent)
  const burst = useRef<{ id: number; before: Record<string, string>; vars: Record<string, string> } | null>(null);
  // `flush` must be ONE function for the component's life: the unmount cleanup
  // below closes over it, and a cleanup that re-armed on every render would fire
  // on the very re-render the live `setThemes` causes — measured in the drive:
  // the PATCH landed within 120 ms of the keystroke, a drag would PATCH per tick.
  // `toast` and `t` arrive with a new identity per SettingsView render, so they
  // are read through a ref written after each render, never listed as deps.
  const live = useRef({ setThemes, toast, t });
  useEffect(() => {
    live.current = { setThemes, toast, t };
  });
  const flush = useCallback(() => {
    timer.current = null;
    const b = burst.current;
    burst.current = null;
    if (!b) return;
    api
      .patch(`/themes/${b.id}`, { vars: b.vars })
      .then((updated: unknown) => {
        const row = updated as ThemeRowLike;
        live.current.setThemes((prev) => prev.map((x) => (x.id === row.id ? row : x)));
      })
      .catch((e: unknown) => {
        const { toast: say, t: tr, setThemes: set } = live.current;
        const msg = e instanceof Error && e.message ? e.message : tr("theme.toast.save_failed", { fallback: "save failed" });
        say("⚠ " + msg);
        set((prev) => prev.map((x) => (x.id === b.id ? { ...x, vars: b.before } : x)));
      });
  }, []);
  // a pending burst is sent when the pane unmounts, never dropped — mount-only,
  // because `flush` is stable
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        flush();
      }
    },
    [flush],
  );
  const commit = (raw: string) => {
    if (!day || !HEX6.test(raw)) return;
    const hex = raw.toLowerCase();
    const vars = { ...(day.vars || {}), "--brand": hex };
    if (!burst.current) burst.current = { id: day.id, before: day.vars || {}, vars };
    else burst.current.vars = vars;
    setThemes((prev) => prev.map((x) => (x.id === day.id ? { ...x, vars } : x)));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, FIRM_BRAND_DEBOUNCE_MS);
  };
  const shown = draft ?? resolved;
  return (
    <div className="field">
      <label>{t("theme.var.brand2", { fallback: "Main colour" })}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="color"
          value={resolved}
          disabled={!day}
          onChange={(e) => {
            setDraft(null);
            commit(e.target.value);
          }}
          style={{
            width: 40,
            height: 28,
            padding: 0,
            border: "1px solid var(--br)",
            borderRadius: RAD.r4,
            background: "var(--bi)",
            cursor: day ? "pointer" : "not-allowed",
          }}
        />
        <input
          className="field-input"
          type="text"
          value={shown}
          disabled={!day}
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            if (HEX6.test(v)) commit(v);
          }}
          onBlur={() => setDraft(null)}
          placeholder={CT.c_4A7CFF}
          style={{
            width: 100,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: FS.s11,
          }}
        />
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: RAD.r4,
            border: "1px solid var(--br)",
            background: resolved,
          }}
        />
      </div>
      <div
        style={{
          fontSize: FS.s9_5,
          color: "var(--tm)",
          marginTop: 4,
          lineHeight: 1.4,
        }}
      >
        {t("settings.help.mainColour", {
          fallback:
            "The firm's colour: the firm's name in the sidebar and in the compact column, and the title of every exported document wear it. It is the Day theme's main colour and every collaborator sees it, whatever theme they run.",
        })}
      </div>
    </div>
  );
}

const SettingsView = ({
  // BRIEF 108 §4b — the search's one-shot focus hint ({ pane, seq } | null)
  // and its consumed callback.
  settingsFocus,
  onSettingsFocusDone,
  cc,
  sCc,
  cl,
  sCl,
  ca,
  sCa,
  op,
  sOp,
  ocu,
  sOcu,
  ex,
  sEx,
  ju,
  sJu,
  co,
  sCo,
  te,
  sTe,
  ag,
  sAg,
  cp,
  sCp,
  oc,
  sOc,
  evtTypes,
  tkTypes,
  currentUser,
  sCurrentUser,
  lookback,
  sLookback,
  nwh,
  sNwh,
  permMx,
  sPermMx,
  sfMaps,
  setSfMaps,
  // Phase 30 (D-2) — server-backed saves (FirmSettings), rollback + toast.
  saveSfMaps,
  extTypeMaps,
  setExtTypeMaps,
  saveExtTypeMaps,
  emailClient,
  setEmailClient,
  mtgApp,
  setMtgApp,
  mtgPlatforms,
  setMtgPlatforms,
  mtgDefLoc,
  setMtgDefLoc,
  pollTool,
  setPollTool,
  pollCustomUrl,
  setPollCustomUrl,
  workHours,
  setWorkHours,
  pomInterval,
  setPomInterval,
  // 147 §1.6 — the mute, a persisted pref like the interval beside it.
  pomMuted,
  setPomMuted,
  sfForm,
  setSfForm,
  xtForm,
  setXtForm,
  etForm,
  sEtForm,
  tkForm,
  setTkForm,
  fRef,
  s: _sRaw,
  toast,
  caseTypesList,
  docTypesList,
  titlesList,
  tk,
  clientTypesList,
  caseStatusesList,
  barsList,
  countriesList,
  docStatusesList,
  me,
  ar,
  op2,
  theme,
  setTheme,
  personalTheme,
  setPersonalTheme,
  textSize,
  setTextSize,
  autoNightSwitch,
  setAutoNightSwitch,
  dayStart,
  setDayStart,
  nightStart,
  setNightStart,
  themes,
  setThemes,
  firmLogo,
  setFirmLogo,
  firmName,
  setFirmName,
  firmSettings,
  saveFirmSettings,
  applyFirmSettings,
  appFont,
  setAppFont,
  linkTypesList,
  stageTypesList,
  taskStatusesList,
  itemLevelList,
  reminderOptsList,
  maxTimers,
  setMaxTimers,
  timerRound,
  setTimerRound,
}: any) => {
  // hp() is a local proxy from the legacy permId vocabulary to the M+2b
  // section keys. Admin always wins; known coarse keys map to the right
  // section keys; unknown keys default-allow (preserves the prototype's
  // silent-pass for unrecognised permIds — callers within this view
  // still pass legacy strings).
  const _perms = usePermissions();
  const _isAdminHere = !!_perms.viewer?.isAdmin;
  // 81 §8 — what is currently cut off. Held HERE rather than in each panel
  // because two panels read it and one of them writes it: the incident screen
  // needs to know which people are killed, and the panic switch needs to know
  // whether the firm is. Two independent fetches would let the two disagree
  // about the same fact for as long as one of them was stale.
  const [_killState, _setKillState] = useState<KillState | null>(null);
  const _reloadKillState = useCallback(() => {
    if (!_isAdminHere) return;
    getKillState().then(_setKillState).catch(() => { /* the panels show nothing armed */ });
  }, [_isAdminHere]);
  useEffect(() => { _reloadKillState(); }, [_reloadKillState]);
  // Phase auth-c2 Stage 3 — sg() is the canonical per-Settings-panel
  // permission check. Admin always wins; otherwise checks the per-panel
  // section key. Used by every panel-render conditional below.
  // P20f — the hide list qualifies the admin short-circuit: `_isAdminHere ||`
  // skips can() (which already denies a hidden key), and hiding is exactly the
  // rule an administrator does not override — it is product shape, not
  // permission. Live case here: the four settings.rates.* OfficialRate panels
  // are module-bound to 'interest-be'.
  const sg = (k: any) => !_perms.hidden(k) && (_isAdminHere || _perms.can(k));
  // Helper: at-least-one of a key list (for subsection-header rendering).
  const sgAny = (keys: any) => keys.some((k: any) => sg(k));
  // Actor-admin signal (the originally-logged-in user, not the simulated
  // viewer). Reads lx:authUser, falls back to the collaborator row. NOT
  // used for visibility — admin-only panels gate on _isAdminHere so
  // simulation shows exactly what the simulated role sees. Kept for
  // actor-identity checks (self-toggle guard, "this is you" tag) and
  // for internal admin-overlay props passed to child panels.
  const _meAuth = (() => {
    try {
      const raw = localStorage.getItem("lx:authUser");
      if (raw) {
        const u = JSON.parse(raw);
        return u && u.id != null ? u : null;
      }
    } catch {}
    return null;
  })();
  const _meAuthIsAdmin = (() => {
    if (!_meAuth) return false;
    if (_meAuth.isAdmin === true) return true;
    const row = co.find((c: { id: number; isAdmin?: boolean }) => c.id === _meAuth.id);
    return !!(row && row.isAdmin);
  })();
  // Pre-compute subsection visibility (a header renders iff at least one
  // panel inside renders). The 6 matrix-driven subsections fall through
  // sgAny over their grantable keys. Personal Preferences picks up the
  // simulated-identity admin overlay so the Current User / "I am" panel
  // header still shows for an admin viewer (and hides under simulation,
  // matching the simulated role's view). Users & Roles is admin-only on
  // the simulated identity — under "Viewing as" a non-admin it hides, so
  // simulation shows exactly what that role would see; the header
  // simulator (App.jsx, _authIsAdmin) is the unconditional escape hatch.
  const _SG = {
    // 80 §1 — UNCONDITIONAL, and it used to be `sgAny` over the nine
    // `settings.personal.*` keys, or `_isAdminHere`.
    //
    // The second-step card this family now contains is NOT GRANTABLE: an
    // administrator who could withhold it would be withholding a person's
    // ability to secure his own account, and under mode `optional` that
    // reinstates 78b by grant rather than by omission. A header renders iff at
    // least one panel inside it renders, and that panel always renders — so the
    // header always renders, and computing it from the nine keys would be
    // computing an answer that is already known.
    //
    // The nine keys are not lost: each is still read at its own panel's
    // `sg("settings.personal.…")` call site, which is where it decides anything.
    personal: true,
    branding: true, // Phase 44 — colour-editing subset is available to all users
    integrations: sgAny([
      "settings.integrations.m365",
      "settings.integrations.mtgplat",
      "settings.integrations.folderTpl",
      "settings.integrations.sync",
      "settings.integrations.ext",
    ]),
    usersRoles: _isAdminHere,
    billing: sgAny([
      "settings.billing.firm",
      "settings.billing.bank",
      "settings.billing.invNum",
      "settings.billing.invTerms",
      "settings.billing.invGC",
      "settings.billing.invLogo",
      "settings.billing.invPayTpl",
      "settings.billing.invTpl",
    ]),
    rates: sgAny([
      "settings.rates.emailFormula",
      "settings.rates.whatsappFormula",
      "settings.rates.docFormula",
      "settings.rates.emailCo",
      "settings.rates.legalInterest",
      "settings.rates.commercialInterest",
      "settings.rates.listingFees",
      "settings.rates.proceduralCompensation",
    ]),
    customLists: sgAny([
      "settings.customLists.taxonomies",
      "settings.customLists.docTemplates",
    ]),
    // Phase 19 — Places admin subsection. Uses lateral.places.edit
    // (the catalog-edit gate) since the panel both lists places and
    // edits their base-availability.
    places: sgAny(["lateral.places.edit"]),
    data: sgAny([
      "settings.data.backupDest",
      "settings.data.backup",
      "settings.data.restore",
      "settings.data.xlsx",
      "settings.data.i18n",
    ]),
  };
  const hp = (p: any) => {
    if (_isAdminHere) return true;
    switch (p) {
      case "view_case_financials":
        return _perms.can("caseView.financials.view");
      // Phase auth-c2 Stage 3 — these legacy permIds previously mapped to
      // the retired lateral.settingsBusiness coarse gate. They now map to
      // the appropriate per-panel key. Each is a legacy-permId entry point
      // for a single panel.
      case "access_settings":
        // Page-level entry — universally true (the lateral nav itself is
        // universal post-Stage 3).
        return true;
      case "change_theme":
      case "customize_theme":
        return sg("settings.branding.theme");
      case "customize_kb":
        return sg("settings.personal.kb");
      case "change_text_size":
        // No dedicated panel; falls under personal preferences.
        return true;
      default:
        return true;
    }
  };
  // SettingsView defines its list editors (SimpleListEditor, CodedListEditor,
  // etc.) as nested const arrow functions inside this component body, which
  // means every re-render of SettingsView creates fresh component types.
  // React treats them as different types and unmounts/remounts the editor
  // subtrees — in practice, the `.C` container's scroll is reset because the
  // DOM is briefly torn down and rebuilt. Rather than hoist every editor,
  // shadow the `s` prop with a wrapper that captures the scrollTop before
  // the save fires and restores it on the next two animation frames.
  const s = (k: any, d: any, fn: any) => {
    const el = document.querySelector(".content");
    const y = el ? el.scrollTop : 0;
    const wy = window.scrollY;
    _sRaw(k, d, fn);
    requestAnimationFrame(() => {
      if (el) el.scrollTop = y;
      window.scrollTo(0, wy);
      requestAnimationFrame(() => {
        if (el && el.scrollTop !== y) el.scrollTop = y;
        if (window.scrollY !== wy) window.scrollTo(0, wy);
      });
    });
  };
  // Catch-all scroll preservation — covers state updates that don't route
  // through s() (e.g. setEmailClient, setPomInterval, setTheme). Track the
  // user's scroll in a ref via a scroll listener, then after every render
  // restore the container scrollTop if it reset to 0 while we had a
  // non-zero position tracked. Unmount/remount of inline-declared sub-
  // components inside SettingsView is what drops the scroll; this hook
  // is a lightweight shim that doesn't require hoisting the editors.
  const _scrollRef = useRef(0);
  useEffect(() => {
    const el = document.querySelector(".content");
    if (!el) return;
    const onScroll = () => {
      _scrollRef.current = el.scrollTop;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    _scrollRef.current = el.scrollTop;
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useLayoutEffect(() => {
    const el = document.querySelector(".content");
    if (!el) return;
    if (_scrollRef.current > 0 && el.scrollTop < _scrollRef.current - 2) {
      el.scrollTop = _scrollRef.current;
    }
  });

  // Default state: every pane starts collapsed on mount (chevron ▶, content
  // hidden). An id absent from panOpen is treated as collapsed by cls(), so an
  // empty initial map collapses all panes. No persistence (component-local
  // state, resets on navigation).
  const [panOpen, setPanOpen] = useState<any>(() => ({}));
  const { t: _t } = useTranslation();
  // 213 — the translator typed at the seam, for the panes 213 adds (the hook's `t` reads as `any` here)
  const _tt = _t as Translate;
  const tog = (id: any) => setPanOpen((p: any) => ({ ...p, [id]: !p[id] }));
  const sh4 = (id: any, title: any, extra?: any) => (
    <h4
      className="settings-pane-title"
      style={{ margin: 0, ...(extra || {}) }}
      onClick={() => tog(id)}
      /* BRIEF 108 §4b — the search's scroll anchor, one attribute for every
         pane this helper renders. */
      data-spane={id}
    >
      <span className="settings-chevron">{panOpen[id] ? "▼" : "▶"}</span>
      {title}
    </h4>
  );
  const cls = (id: any) => (panOpen[id] ? "" : " cl");
  /* BRIEF 108 §4b — the one-shot focus hint this view never had: a search
     result names a pane; the pane opens and the card scrolls into view. The
     hint is consumed exactly once (onSettingsFocusDone), so a later manual
     collapse is not fought. The rAF-then-timeout is for the open's re-render:
     scrollIntoView needs the expanded card's box. */
  useEffect(() => {
    if (!settingsFocus || !settingsFocus.pane) return;
    setPanOpen((p: Record<string, boolean>) => ({ ...p, [settingsFocus.pane]: true }));
    const tmr = setTimeout(() => {
      document
        .querySelector(`[data-spane="${settingsFocus.pane}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      onSettingsFocusDone?.();
    }, 120);
    return () => clearTimeout(tmr);
  }, [settingsFocus, onSettingsFocusDone]);
  const PRESETS = [
    CT.c_4A7CFF,
    CT.c_34D399,
    CT.c_FBBF24,
    CT.c_F87171,
    CT.c_A78BFA,
    CT.c_8892A4,
    CT.c_E8ECF4,
    CT.c_1A1F2B,
  ];
  // colorHistory now lives in Collaborator.preferences.colorHistory; the array
  // is read on every render and writes route through setPref via the prefs hook.
  const { prefs: _prefsLocal, setPref: _setPrefLocal } = usePreferences();
  const colorHist = Array.isArray(_prefsLocal.colorHistory)
    ? _prefsLocal.colorHistory
    : [];
  const pushColor = (c: any) => {
    const hex = c.toUpperCase();
    const nh = [hex, ...colorHist.filter((x) => x !== hex)].slice(0, 10);
    _setPrefLocal("colorHistory", nh);
  };
  const CP = ({ value, onChange, small }: any) => {
    const sz = small ? { w: 24, h: 20 } : { w: 32, h: 24 };
    return (
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          flexDirection: "column",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <input
          type="color"
          value={value || CT.c_888888}
          style={{
            width: sz.w,
            height: sz.h,
            padding: 1,
            background: "none",
            border: "1px solid var(--br)",
            borderRadius: small ? 4 : 5,
            cursor: "pointer",
          }}
          onChange={(e) => {
            onChange(e.target.value);
            pushColor(e.target.value);
          }}
        />
        <div
          style={{ display: "flex", gap: 1, flexWrap: "wrap", maxWidth: 90 }}
        >
          {PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              style={{
                width: 8,
                height: 8,
                borderRadius: RAD.pill,
                background: c,
                border:
                  value?.toUpperCase() === c
                    ? `1.5px solid ${CT.c_fff}`
                    : "1px solid rgba(128,128,128,.3)",
                cursor: "pointer",
                padding: 0,
              }}
              onClick={() => {
                onChange(c);
                pushColor(c);
              }}
              title={c}
            />
          ))}
        </div>
        {colorHist.length > 0 && (
          <div
            style={{ display: "flex", gap: 1, flexWrap: "wrap", maxWidth: 90 }}
          >
            {colorHist.map((c) => (
              <button
                key={c}
                type="button"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: RAD.pill,
                  background: c,
                  border:
                    value?.toUpperCase() === c
                      ? `1.5px solid ${CT.c_fff}`
                      : "1px solid rgba(128,128,128,.2)",
                  cursor: "pointer",
                  padding: 0,
                  opacity: 0.7,
                }}
                onClick={() => {
                  onChange(c);
                }}
                title={c}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const backup = () => {
    const d = {
      v: 1,
      date: new Date().toISOString(),
      cl,
      ca,
      cc,
      op,
      ocu,
      ex,
      ju,
      co,
      te,
      ag,
      cp,
      oc,
      et: evtTypes,
    };
    const blob = new Blob([JSON.stringify(d, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lexcrm-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast(
      _t("settings.toast.backupDownloaded", { fallback: "Backup downloaded" }),
    );
  };
  const restore = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const d = JSON.parse((ev.target as any).result);
        if (!d.cl || !d.ca) throw new Error("Invalid backup");
        s(SK.cl, d.cl || [], sCl);
        s(SK.ca, d.ca || [], sCa);
        s(SK.op, d.op || [], sOp);
        s(SK.ocu, d.ocu || d.ol || [], sOcu);
        s(SK.ex, d.ex || [], sEx);
        s(SK.ju, d.ju || [], sJu);
        s(SK.co, d.co || [], sCo);
        s(SK.te, d.te || [], sTe);
        s(SK.ag, d.ag || [], sAg);
        s(SK.cp, d.cp || [], sCp);
        s(SK.oc, d.oc || [], sOc);
        if (d.cc) s(SK.cc, d.cc, sCc);
        toast(_t("settings.toast.dataRestored", { fallback: "Data restored" }));
      } catch {
        toast(
          _t("settings.toast.invalidBackup", {
            fallback: "⚠ Invalid backup file",
          }),
        );
      }
      e.target.value = "";
    };
    reader.readAsText(file);
  };
  return (
    /* 190 round 6 [X](a) (ruled 2026-09-16, held back from round 5) — THE
       PANES FOLLOW THE WINDOW, all the same: the `maxWidth: 960` that capped
       the whole settings page is gone. The cost is ruled: "fine even for
       two-row panes, no other choice to keep it tidy". */
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Phase auth-c2 Stage 3 — each subsection header renders iff the
          user holds ≥1 grantable gate inside it (Personal Preferences is
          universal; Users & Roles stays admin-only). */}
      {_SG.personal && (
        <div className="settings-section-header" style={{ order: 10 }}>
          {_t("settings.section.personalPreferences", {
            fallback: "Personal Preferences",
          })}
        </div>
      )}
      {_SG.branding && (
        <div className="settings-section-header" style={{ order: 30 }}>
          {_t("settings.section.brandingTheme", { fallback: "Branding & Theme" })}
        </div>
      )}
      {_SG.integrations && (
        <div className="settings-section-header" style={{ order: 40 }}>
          {_t("settings.section.integrations", { fallback: "Integrations" })}
        </div>
      )}
      {_SG.usersRoles && (
        <div className="settings-section-header" style={{ order: 50 }}>
          {_t("settings.section.usersRoles", { fallback: "Users & Roles" })}
        </div>
      )}
      {_SG.billing && (
        <div className="settings-section-header" style={{ order: 60 }}>
          {_t("settings.section.billingInvoicing", {
            fallback: "Billing & Invoicing",
          })}
        </div>
      )}
      {_SG.rates && (
        <div className="settings-section-header" style={{ order: 70 }}>
          {_t("settings.section.rates", { fallback: "Rates" })}
        </div>
      )}
      {_SG.customLists && (
        <div className="settings-section-header" style={{ order: 80 }}>
          {_t("settings.section.customLists", { fallback: "Custom Lists" })}
        </div>
      )}
      {/* Phase 8 F3 — Translations section header deleted; its two subsections
    (Manage Translations, Operational data Excel) moved into Data & Backups
    via order reassignment (orders 103 + 104 below). */}
      {_SG.places && (
        <div className="settings-section-header" style={{ order: 90 }}>
          {_t("settings.section.places", { fallback: "Places" })}
        </div>
      )}
      {_SG.places && (
        <div
          className={`detail-card settings-pane${cls("places")}`}
          style={{ order: 92 }}
        >
          {sh4("places", _t("settings.panel.places", { fallback: "Places & base availability" }))}
          <PlacesAdminPanel toast={toast} />
        </div>
      )}
      {/* 58 §5b — the schedule axis. Admin-only, like workHours: the route
          routes `scheduleAxis` to the same `__admin__` sentinel. */}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("schedAxis")}`}
          style={{ order: 93 }}
        >
          {sh4(
            "schedAxis",
            _t("settings.panel.scheduleAxis", { fallback: "Schedule axis" }),
          )}
          <ScheduleAxisPanel
            firmSettings={firmSettings}
            saveFirmSettings={saveFirmSettings}
            t={_t}
            toast={toast}
            canEdit={_isAdminHere}
          />
        </div>
      )}
      {_SG.data && (
        <div className="settings-section-header" style={{ order: 100 }}>
          {_t("settings.section.dataBackups", { fallback: "Data & Backups" })}
        </div>
      )}
      {/* Phase auth-c2 Stage 3 — Current User / "I am" simulation is
          impersonation: admin-only by guardrail (no grantable key). Gated
          on the simulated-identity admin flag so under "Viewing as" a
          non-admin the panel hides (matching what that role would see).
          The header simulator (App.jsx, _authIsAdmin) is actor-based and
          remains the escape hatch back to the admin identity. */}
      {_isAdminHere && (
      <div
        className={`detail-card settings-pane${cls("user")}`}
        style={{ order: 12 }}
      >
        {sh4(
          "user",
          _t("settings.panel.currentUser", { fallback: "Current User" }),
        )}
        <p
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            margin: "4px 0 10px",
            lineHeight: 1.5,
          }}
        >
          {_t("settings.desc.currentUser", {
            fallback:
              "Select your profile to enable personalised agenda views. Partners see all events and can filter.",
          })}
        </p>
        <div className="field">
          <label>
            {_t("settings.currentUser.label", { fallback: "I am" })}
          </label>
          <MenuSelect
            className="field-input"
            value={currentUser ? String(currentUser) : ""}
            onChange={(picked) => {
              const v = picked ? +picked : null;
              sCurrentUser(v);
              sv(SK.cu, v);
            }}
            options={[
              [
                "",
                _t("settings.currentUser.notConfigured", {
                  fallback: "— Not configured —",
                }),
              ] as const,
              /* 75 §4 — "I am" is becoming somebody: only active people are
                 offered (isActiveCo — the server refuses to simulate a
                 suspended or archived collaborator). */
              ...co.filter(isActiveCo).map(
                (c: any) =>
                  [String(c.id), `${pname(c)} (${roleLabel(c.role)})`] as const,
              ),
            ]}
          />
        </div>
        {currentUser &&
          (() => {
            const c = co.find((x: { id: number }) => x.id === currentUser);
            return c ? (
              <p style={{ fontSize: FS.s10, color: "var(--ts)", marginTop: 6 }}>
                {_t("settings.currentUser.loggedInAs", {
                  fallback: "Logged in as",
                })}{" "}
                {pname(c)} ·{" "}
                {/* 174 §6.6 — this sentence DESCRIBED the agenda filter that
                    read `role.firmWideVisibility`, a field Phase 24 dropped
                    (`schema.prisma:2493`). `undefined === true` is always
                    false, so every non-admin was told "Can only see events
                    assigned to you" whatever the firm's scope said — and the
                    agenda now shows exactly what the SERVER scoped, which is
                    `FirmSettings.surfaceScopes` resolved through
                    `buildRowGate`. So the sentence reads the same value the
                    server resolved from, shipped on /permissions/me since
                    Phase 24 and read here for the first time. */}
                {_perms.viewer?.isAdmin
                  ? _t("settings.currentUser.adminAll", {
                      fallback: "Admin — sees all events",
                    })
                  : _perms.surfaceScopes?.generalAgenda === "firm-wide"
                    ? _t("settings.currentUser.agendaFirmWide", {
                        fallback: "Sees the firm's whole agenda",
                      })
                    : _t("settings.currentUser.agendaScoped", {
                        fallback:
                          "Sees events on your matters, and events assigned to you",
                      })}
              </p>
            ) : null;
          })()}
      </div>
      )}
      {sg("settings.integrations.m365") && <M365ConnectionPanel cls={cls} sh4={sh4} toast={toast} firmName={firmName} />}
      {/* 213 §4 — the ready-made shortcuts' iCloud links, beside Microsoft 365:
          written by an administrator (PUT /api/settings is admin-only), shown in
          « Mes connexions » under the shortcuts' key. */}
      {_isAdminHere && (
        <div className={`detail-card settings-pane${cls("shortcutLinks")}`} style={{ order: 41 }}>
          {sh4("shortcutLinks", _tt("settings.panel.shortcutLinks", { fallback: "Shortcut links (iCloud)" }))}
          {!cls("shortcutLinks") && (
            <Suspense fallback={null}>
              <ShortcutLinksPanel toast={toast as ToastFn} />
            </Suspense>
          )}
        </div>
      )}
      {sg("settings.integrations.m365") && <BulkMailerPanel cls={cls} sh4={sh4} toast={toast} />}
      {sg("settings.personal.pwa") && <PwaInstallPanel cls={cls} sh4={sh4} toast={toast} firmName={firmName} />}
      {(sg("settings.rates.emailFormula") ||
        sg("settings.rates.whatsappFormula") ||
        sg("settings.rates.docFormula") ||
        sg("settings.rates.emailCo") ||
        _isAdminHere) && (
        <RatesAdminBlock
          cls={cls}
          sh4={sh4}
          _meAuth={_meAuth}
          co={co}
          sCo={sCo}
          currentUser={currentUser}
          toast={toast}
          sg={sg}
          s={s}
          _isAdminHere={_isAdminHere}
          _meAuthIsAdmin={_meAuthIsAdmin}
        />
      )}
      {sg("settings.data.i18n") && <I18nDataPanel cls={cls} sh4={sh4} toast={toast} />}
      {sg("settings.data.xlsx") && (
        <div
          className={`detail-card settings-pane${cls("phase4Xlsx")}`}
          style={{ order: 103 }}
        >
          {sh4(
            "phase4Xlsx",
            _t("phase4.xlsx.panel.title", {
              fallback: "Operational data — Excel round-trip",
            }),
          )}
          <SettingsXlsxHubPanel toast={toast} />
        </div>
      )}
      {sg("settings.data.backupDest") && (
        <div
          className={`detail-card settings-pane${cls("phase4Backup")}`}
          style={{ order: 100 }}
        >
          {sh4(
            "phase4Backup",
            _t("phase4.backup.panel.title", {
              fallback: "Daily backup — push destinations",
            }),
          )}
          <BackupDestinationsPanel toast={toast} />
        </div>
      )}
      {sg("settings.data.backup") && (
      <div
        className={`detail-card settings-pane${cls("backup")}`}
        style={{ order: 101 }}
      >
        {sh4("backup", _t("settings.panel.backup", { fallback: "Backup" }))}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 10px",
            lineHeight: 1.6,
          }}
        >
          {_t("settings.desc.backup", {
            fallback:
              "Export all your data as a JSON file you can save as a backup.",
          })}
        </p>
        <button className="btn btn-primary" onClick={backup}>
          {_t("settings.btn.downloadBackup", { fallback: "↓ Download Backup" })}
        </button>
      </div>
      )}
      {sg("settings.data.restore") && (
      <div
        className={`detail-card settings-pane${cls("restore")}`}
        style={{ order: 102 }}
      >
        {sh4("restore", _t("settings.panel.restore", { fallback: "Restore" }))}
        <p
          style={{
            fontSize: FS.s11_5,
            color: "var(--ts)",
            margin: "6px 0 4px",
            lineHeight: 1.6,
          }}
        >
          {_t("settings.desc.restore", {
            fallback: "Restore data from a backup file.",
          })}
        </p>
        <p style={{ fontSize: FS.s10_5, color: "var(--rd)", marginBottom: 10 }}>
          {_t("settings.warn.restoreOverwrite", {
            fallback: "Warning: this will overwrite all current data.",
          })}
        </p>
        <input
          ref={fRef}
          type="file"
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={restore}
        />
        <button className="btn btn-ghost" onClick={() => fRef.current.click()}>
          {_t("settings.btn.restoreBackup", {
            fallback: "↑ Restore from Backup",
          })}
        </button>
      </div>
      )}
      {sg("settings.integrations.sync") && (
      <div
        className={`detail-card settings-pane${cls("sync")}`}
        style={{ order: 43 }}
      >
        {sh4(
          "sync",
          _t("settings.panel.syncFolderMappings", {
            fallback: "Sync Folder Mappings",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            margin: "4px 0 10px",
            lineHeight: 1.5,
          }}
        >
          {_t("settings.desc.syncFolders", {
            fallback:
              "Map local sync folder paths to cloud base URLs. When you paste a local file path in the document form, the matching mapping is used to auto-generate the cloud link.",
          })}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 28px",
            gap: 4,
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {_t("settings.col.label", { fallback: "Label" })}
          </span>
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {_t("settings.col.localBasePath", { fallback: "Local Base Path" })}
          </span>
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {_t("settings.col.cloudBaseUrl", { fallback: "Cloud Base URL" })}
          </span>
          <span />
        </div>
        {sfMaps.map((m: any, i: any) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 28px",
              gap: 4,
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <input
              className="field-input"
              style={{ padding: "3px 6px", fontSize: FS.s10_5 }}
              value={m.label}
              onChange={(e) => {
                const u = [...sfMaps];
                u[i] = { ...u[i], label: e.target.value };
                saveSfMaps(u);
              }}
            />
            <input
              className="field-input"
              style={{
                padding: "3px 6px",
                fontSize: FS.s10,
                fontFamily: "'JetBrains Mono',monospace",
              }}
              value={m.localBase}
              onChange={(e) => {
                const u = [...sfMaps];
                u[i] = { ...u[i], localBase: e.target.value };
                saveSfMaps(u);
              }}
            />
            <input
              className="field-input"
              style={{ padding: "3px 6px", fontSize: FS.s10 }}
              value={m.cloudBase}
              onChange={(e) => {
                const u = [...sfMaps];
                u[i] = { ...u[i], cloudBase: e.target.value };
                saveSfMaps(u);
              }}
            />
            <button
              className="btn btn-danger"
              style={{ padding: "1px 4px", fontSize: FS.s9 }}
              onClick={() => {
                const u = sfMaps.filter((_: any, j: any) => j !== i);
                saveSfMaps(u);
              }}
            >
              <Trash2 size={I.s} />
            </button>
          </div>
        ))}
        <div
          style={{
            borderTop: "1px solid var(--br)",
            marginTop: 8,
            paddingTop: 8,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr auto",
            gap: 4,
            alignItems: "center",
          }}
        >
          <input
            className="field-input"
            style={{ padding: "3px 6px", fontSize: FS.s10_5 }}
            placeholder={_t("settings.placeholder.syncLabel", {
              fallback: "e.g. OneDrive Firm",
            })}
            value={sfForm.label}
            onChange={(e) =>
              setSfForm((p: any) => ({ ...p, label: e.target.value }))
            }
          />
          <input autoComplete="off" /* 207 §18 */
            className="field-input no-contact-autofill"
            style={{
              padding: "3px 6px",
              fontSize: FS.s10,
              fontFamily: "'JetBrains Mono',monospace",
            }}
            placeholder="/Users/name/OneDrive - Firm/"
            value={sfForm.localBase}
            onChange={(e) =>
              setSfForm((p: any) => ({ ...p, localBase: e.target.value }))
            }
          />
          <input
            className="field-input"
            style={{ padding: "3px 6px", fontSize: FS.s10 }}
            placeholder="https://firm.sharepoint.com/…"
            value={sfForm.cloudBase}
            onChange={(e) =>
              setSfForm((p: any) => ({ ...p, cloudBase: e.target.value }))
            }
          />
          <button
            className="btn btn-primary"
            style={{ padding: "3px 8px", fontSize: FS.s10, whiteSpace: "nowrap" }}
            onClick={() => {
              if (!sfForm.localBase.trim()) return;
              const u = [...sfMaps, { ...sfForm }];
              saveSfMaps(u);
              setSfForm({ label: "", localBase: "", cloudBase: "" });
            }}
          >
            {_t("settings.btn.add", { fallback: "+ Add" })}
          </button>
        </div>
      </div>
      )}
      {sg("settings.integrations.ext") && (
      <div
        className={`detail-card settings-pane${cls("ext")}`}
        style={{ order: 44 }}
      >
        {sh4(
          "ext",
          _t("settings.panel.fileExtensions", {
            fallback: "File Extension → Document Type",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            margin: "4px 0 10px",
            lineHeight: 1.5,
          }}
        >
          {_t("settings.desc.fileExt", {
            fallback:
              "When browsing a file, the document type is auto-detected from its extension using this mapping.",
          })}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "80px 1fr 28px",
            gap: 4,
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {_t("settings.col.extension", { fallback: "Extension" })}
          </span>
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {_t("settings.col.documentType", { fallback: "Document Type" })}
          </span>
          <span />
        </div>
        {extTypeMaps.map((m: any, i: any) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "80px 1fr 28px",
              gap: 4,
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <span className="mono" style={{ fontSize: FS.s10_5, color: "var(--ts)" }}>
              .{m.ext}
            </span>
            <MenuSelect
              className="field-input"
              style={{ padding: "3px 6px", fontSize: FS.s11 }}
              value={m.type}
              onChange={(v) => {
                const u = [...extTypeMaps];
                u[i] = { ...u[i], type: v };
                saveExtTypeMaps(u);
              }}
              options={[
                ["pleading", _t("settings.docType.pleading", { fallback: "Pleading" })],
                ["evidence", _t("settings.docType.evidence", { fallback: "Evidence" })],
                ["contract", _t("settings.docType.contract", { fallback: "Contract" })],
                [
                  "expert_report",
                  _t("settings.docType.expert_report", {
                    fallback: "Expert Report",
                  }),
                ],
                [
                  "correspondence",
                  _t("settings.docType.correspondence", {
                    fallback: "Correspondence",
                  }),
                ],
                ["other", _t("settings.docType.other", { fallback: "Other" })],
              ]}
            />
            <button
              className="btn btn-danger"
              style={{ padding: "1px 4px", fontSize: FS.s9 }}
              onClick={() => {
                const u = extTypeMaps.filter((_: any, j: any) => j !== i);
                saveExtTypeMaps(u);
              }}
            >
              <Trash2 size={I.s} />
            </button>
          </div>
        ))}
        <div
          style={{
            borderTop: "1px solid var(--br)",
            marginTop: 8,
            paddingTop: 8,
            display: "grid",
            gridTemplateColumns: "80px 1fr auto",
            gap: 4,
            alignItems: "center",
          }}
        >
          <input
            className="field-input"
            style={{
              padding: "3px 6px",
              fontSize: FS.s11,
              fontFamily: "'JetBrains Mono',monospace",
            }}
            placeholder="ext"
            value={xtForm.ext}
            onChange={(e) =>
              setXtForm((p: any) => ({
                ...p,
                ext: e.target.value.toLowerCase().replace(/^\./, ""),
              }))
            }
            maxLength={10}
          />
          <MenuSelect
            className="field-input"
            style={{ padding: "3px 6px", fontSize: FS.s11 }}
            value={xtForm.type}
            onChange={(v) => setXtForm((p: any) => ({ ...p, type: v }))}
            options={[
              ["pleading", _t("settings.docType.pleading", { fallback: "Pleading" })],
              ["evidence", _t("settings.docType.evidence", { fallback: "Evidence" })],
              ["contract", _t("settings.docType.contract", { fallback: "Contract" })],
              [
                "expert_report",
                _t("settings.docType.expert_report", {
                  fallback: "Expert Report",
                }),
              ],
              [
                "correspondence",
                _t("settings.docType.correspondence", {
                  fallback: "Correspondence",
                }),
              ],
              ["other", _t("settings.docType.other", { fallback: "Other" })],
            ]}
          />
          <button
            className="btn btn-primary"
            style={{ padding: "3px 8px", fontSize: FS.s10, whiteSpace: "nowrap" }}
            onClick={() => {
              if (!xtForm.ext.trim()) return;
              if (extTypeMaps.find((x: any) => x.ext === xtForm.ext)) {
                toast(
                  _t("settings.toast.extAlreadyMapped", {
                    fallback: "⚠ Extension already mapped",
                  }),
                );
                return;
              }
              const u = [...extTypeMaps, { ...xtForm }];
              saveExtTypeMaps(u);
              setXtForm({ ext: "", type: "pleading" });
            }}
          >
            {_t("settings.btn.add", { fallback: "+ Add" })}
          </button>
        </div>
      </div>
      )}
      {sg("settings.personal.ag") && (
      <div
        className={`detail-card settings-pane${cls("ag")}`}
        style={{ order: 14 }}
      >
        {sh4("ag", _t("settings.panel.agenda", { fallback: "Agenda" }))}
        <div className="field">
          <label>
            {_t("settings.label.lookback", {
              fallback: "Lookback window (days)",
            })}
          </label>
          <input
            className="field-input"
            type="number"
            min={0}
            max={365}
            style={{ width: 80 }}
            value={lookback}
            onChange={(e) => {
              const v = Math.max(0, +e.target.value || 0);
              sLookback(v);
            }}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.lookback", {
            fallback:
              "Events from the last N days are always visible. Set to 0 to show only today and future events.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.dash") && (
      <div
        className={`detail-card settings-pane${cls("dash")}`}
        style={{ order: 15 }}
      >
        {sh4("dash", _t("settings.panel.dashboard", { fallback: "Dashboard" }))}
        <div className="field">
          <label>
            {_t("settings.label.weeklyHours", {
              fallback: "Normal Weekly Hours",
            })}
          </label>
          <input
            className="field-input"
            type="number"
            min={1}
            max={80}
            style={{ width: 80 }}
            value={nwh}
            onChange={(e) => {
              const v = Math.max(1, +e.target.value || 25);
              sNwh(v);
            }}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.weeklyHours", {
            fallback:
              "Target billable hours per week. Used in the dashboard hours counter to colour-code your weekly progress.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.email") && (
      <div
        className={`detail-card settings-pane${cls("email")}`}
        style={{ order: 16 }}
      >
        {sh4(
          "email",
          _t("settings.panel.emailClient", { fallback: "Email Client" }),
        )}
        <div className="field">
          <label>
            {_t("settings.label.defaultEmailClient", {
              fallback: "Default email client for Mailto buttons",
            })}
          </label>
          <MenuSelect
            className="field-input"
            value={emailClient}
            onChange={(v) => {
              setEmailClient(v);
            }}
            options={[
              [
                "mailto",
                _t("settings.opt.systemMailto", {
                  fallback: "System default (mailto:)",
                }),
              ],
              [
                "outlook",
                _t("settings.opt.outlookWeb", {
                  fallback: "Outlook Web (outlook.office.com)",
                }),
              ],
              [
                "gmail",
                _t("settings.opt.gmail", {
                  fallback: "Gmail (mail.google.com)",
                }),
              ],
            ]}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.emailClient", {
            fallback:
              "Choose which email client opens when you click Mailto buttons in the case Parties pane.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.mtg") && (
      <div
        className={`detail-card settings-pane${cls("mtg")}`}
        style={{ order: 17 }}
      >
        {sh4(
          "mtg",
          _t("settings.panel.meetingApp", { fallback: "Meeting Application" }),
        )}
        <div className="field">
          <label>
            {_t("settings.label.defaultMtgApp", {
              fallback: "Default app for scheduling meetings",
            })}
          </label>
          <MenuSelect
            className="field-input"
            value={mtgApp}
            onChange={(v) => {
              setMtgApp(v);
            }}
            options={[
              [
                "ics",
                _t("settings.opt.icsDefault", {
                  fallback: "System default (.ics file download)",
                }),
              ],
              ["teams", _t("settings.opt.teams", { fallback: "Microsoft Teams" })],
              [
                "outlook",
                _t("settings.opt.outlookCal", {
                  fallback: "Outlook Web Calendar",
                }),
              ],
              [
                "google",
                _t("settings.opt.googleCal", { fallback: "Google Calendar" }),
              ],
            ]}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.mtgApp", {
            fallback:
              "Choose which app opens when you create a meeting from a case. Also determines whether the agenda event is logged as REU (physical) or VIR (virtual).",
          })}
        </p>
      </div>
      )}
      {sg("settings.integrations.mtgplat") && (
      <div
        className={`detail-card settings-pane${cls("mtgplat")}`}
        style={{ order: 42 }}
      >
        {sh4(
          "mtgplat",
          _t("settings.panel.virtualMeetings", {
            fallback: "Virtual Meeting Platforms",
          }),
        )}
        <p
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            margin: "4px 0 10px",
            lineHeight: 1.5,
          }}
        >
          {_t("settings.desc.virtualMtgs", {
            fallback:
              "Enable or disable virtual meeting platforms available when scheduling meetings.",
          })}
        </p>
        {[
          { k: "teams", label: "Microsoft Teams" },
          { k: "zoom", label: "Zoom" },
          { k: "gmeet", label: "Google Meet" },
        ].map((p) => (
          <div
            key={p.k}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0",
              borderBottom: "1px solid rgba(255,255,255,.04)",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: 1,
                fontSize: FS.s11,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!mtgPlatforms[p.k]}
                onChange={() => {
                  const u = { ...mtgPlatforms, [p.k]: !mtgPlatforms[p.k] };
                  setMtgPlatforms(u);
                }}
              />
              {p.label}
            </label>
            {p.k === "zoom" && mtgPlatforms.zoom && (
              <input
                className="field-input"
                style={{
                  width: 200,
                  padding: "3px 6px",
                  fontSize: FS.s10,
                  margin: 0,
                }}
                placeholder={_t("settings.placeholder.zoomUrl", {
                  fallback: "Zoom meeting URL template",
                })}
                value={mtgPlatforms.zoomUrl || ""}
                onChange={(e) => {
                  const u = { ...mtgPlatforms, zoomUrl: e.target.value };
                  setMtgPlatforms(u);
                }}
              />
            )}
          </div>
        ))}
        <div className="field" style={{ marginTop: 8 }}>
          <label>
            {_t("settings.label.defaultMtgLoc", {
              fallback: "Default Meeting Location",
            })}
          </label>
          <input
            className="field-input"
            value={mtgDefLoc}
            onChange={(e) => {
              setMtgDefLoc(e.target.value);
            }}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.mtgLoc", {
            fallback: "Pre-filled location for in-person meetings.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.poll") && (
      <div
        className={`detail-card settings-pane${cls("poll")}`}
        style={{ order: 18 }}
      >
        {sh4(
          "poll",
          _t("settings.panel.pollTool", { fallback: "Poll / Scheduling Tool" }),
        )}
        <div className="field">
          <label>
            {_t("settings.label.defaultPollTool", {
              fallback: "Default tool for scheduling polls",
            })}
          </label>
          <MenuSelect
            className="field-input"
            value={pollTool}
            onChange={(v) => {
              setPollTool(v);
            }}
            options={[
              ["doodle", _t("settings.opt.doodle", { fallback: "Doodle" })],
              [
                "findtime",
                _t("settings.opt.findtime", { fallback: "Microsoft FindTime" }),
              ],
              ["calendly", _t("settings.opt.calendly", { fallback: "Calendly" })],
              ["custom", _t("settings.opt.customUrl", { fallback: "Custom URL" })],
            ]}
          />
        </div>
        {pollTool === "custom" && (
          <div className="field">
            <label>
              {_t("settings.label.customUrl", { fallback: "Custom URL" })}
            </label>
            <input
              className="field-input"
              value={pollCustomUrl}
              onChange={(e) => {
                setPollCustomUrl(e.target.value);
              }}
              placeholder="https://…"
            />
          </div>
        )}
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.pollTool", {
            fallback:
              "Choose which scheduling tool opens when you create a poll from a case.",
          })}
        </p>
      </div>
      )}
      {/* 80 §1 (78b) — the second-step card. NOT behind an `sg()` gate, and
          that is the point: see `_SG.personal` above. It is the only enrolment
          surface a person on an `optional` tenant can ever reach. */}
      <div
        className={`detail-card settings-pane${cls("authFactors")}`}
        style={{ order: 22 }}
      >
        {sh4(
          "authFactors",
          _t("settings.panel.secondStep", { fallback: "Second-step sign-in" }),
        )}
        <AuthFactorsCard toast={toast} />
      </div>
      {/* 81 §7 — "The list, in Settings, BESIDE brief 80's card". Not behind an
          `sg()` gate for the same reason that one is not: an administrator who
          could withhold a person's view of his own sign-ins would withhold the
          one thing that lets him notice somebody else using his account. */}
      <div
        className={`detail-card settings-pane${cls("authAttempts")}`}
        style={{ order: 23 }}
      >
        {sh4(
          "authAttempts",
          _t("settings.panel.signInHistory", { fallback: "Your sign-ins" }),
        )}
        <AuthAttemptsCard />
      </div>
      {/* 213 §1 [RULED 31] — the person's own keys to the door, on for every
          role unless a firm writes `settings.personal.door` off. */}
      {sg("settings.personal.door") && (
        <div
          className={`detail-card settings-pane${cls("door")}`}
          style={{ order: 24 }}
        >
          {sh4("door", _tt("settings.panel.door", { fallback: "My connections" }))}
          {/* mounted while the pane is open (`cls` answers "" then) — the key goes when it closes */}
          {!cls("door") && (
            <Suspense fallback={null}>
              <DoorKeysPanel toast={toast as ToastFn} />
            </Suspense>
          )}
        </div>
      )}
      {sg("settings.personal.notif") && (
      <div
        className={`detail-card settings-pane${cls("notif")}`}
        style={{ order: 19 }}
      >
        {sh4(
          "notif",
          _t("settings.panel.notifications", { fallback: "Notifications" }),
        )}
        <div className="field-row">
          <div className="field">
            <label>
              {_t("settings.label.workHoursStart", {
                fallback: "Work hours start",
              })}
            </label>
            <TimeField
              value={workHours.start || "08:00"}
              onChange={(v) => {
                const u = { ...workHours, start: v };
                setWorkHours(u);
              }}
            />
          </div>
          <div className="field">
            <label>
              {_t("settings.label.workHoursEnd", {
                fallback: "Work hours end",
              })}
            </label>
            <TimeField
              value={workHours.end || "19:00"}
              onChange={(v) => {
                const u = { ...workHours, end: v };
                setWorkHours(u);
              }}
            />
          </div>
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.notifications", {
            fallback:
              "Browser notifications and audio beeps are sent only during working hours, Monday–Friday.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.timer") && (
      <div
        className={`detail-card settings-pane${cls("timer")}`}
        style={{ order: 20 }}
      >
        {sh4(
          "timer",
          _t("settings.panel.timerPomodoro", { fallback: "Timer & Pomodoro" }),
        )}
        <div className="field">
          <label>
            {_t("settings.label.pomInterval", {
              fallback: "Pomodoro Interval (minutes)",
            })}
          </label>
          {/* 147 §1.6 — THE FIELD MUST PERMIT 0, AND IT DID NOT. `min={1}` and
              `Math.max(1, +e.target.value || 20)`: the clamp refused zero and
              the `|| 20` turned a cleared box into twenty, so the value that
              switches the pomodoro off could not be typed. Now:
                · an empty box IS zero — the field is controlled, so ignoring ""
                  would re-render the old number and make it impossible to clear
                  and retype;
                · a non-numeric entry changes nothing rather than snapping to a
                  default the user did not ask for;
                · the 120 ceiling stays, and the floor is 0. */}
          <input
            className="field-input"
            type="number"
            min={0}
            max={120}
            style={{ width: 80 }}
            value={pomInterval}
            onChange={(e) => {
              const raw = e.target.value.trim();
              const n = raw === "" ? 0 : Math.round(Number(raw));
              if (!Number.isFinite(n)) return;
              setPomInterval(Math.min(120, Math.max(0, n)));
            }}
          />
        </div>
        {/* 147 §1.6 — the mute, beside the interval it does NOT replace.
            `pomMuted` was `useState(false)` in App with no setter anywhere in
            the tree: dead state, always false, read once by the chime. */}
        <div className="field" style={{ marginTop: 8 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={!!pomMuted}
              onChange={(e) => setPomMuted(e.target.checked)}
            />
            {_t("settings.label.pomMuted", {
              fallback: "Mute the pomodoro chime",
            })}
          </label>
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {/* THE TWO SWITCHES DO DIFFERENT THINGS, and saying so is the point:
              the thing that reached the user was the desktop NOTIFICATION,
              arriving with the app not in front, and a mute that silenced the
              chime alone would be the second version of that complaint. */}
          {_t("settings.help.pomodoro", {
            fallback:
              "Set the interval to 0 to switch the pomodoro off entirely — no chime and no desktop notification. Muting silences the chime only; the notification still arrives.",
          })}
        </p>
        <div className="field" style={{ marginTop: 8 }}>
          <label>
            {_t("settings.label.maxTimers", {
              fallback: "Maximum simultaneous timers",
            })}
          </label>
          <MenuSelect
            className="field-input"
            style={{ width: 80 }}
            value={String(maxTimers || 5)}
            onChange={(picked) => {
              const v = +picked;
              setMaxTimers(v);
            }}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
              (n) => [String(n), String(n)] as const,
            )}
          />
        </div>
        <div className="field" style={{ marginTop: 8 }}>
          <label>
            {_t("settings.label.timerRound", {
              fallback: "Timer rounding increment",
            })}
          </label>
          <MenuSelect
            className="field-input"
            style={{ width: 80 }}
            value={String(timerRound || 5)}
            onChange={(picked) => {
              const v = +picked;
              setTimerRound(v);
            }}
            options={[1, 5, 10, 15].map(
              (n) =>
                [
                  String(n),
                  _t("time.min_only", { mn: n, fallback: `${n} min` }),
                ] as const,
            )}
          />
        </div>
        <p
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {_t("settings.help.pomTimer", {
            fallback:
              "Pomodoro interval triggers a break reminder after continuous work. Timer rounding rounds accumulated time up to the nearest increment when pausing, stopping, or logging. Maximum timers limits simultaneous active timers.",
          })}
        </p>
      </div>
      )}
      {sg("settings.personal.kb") && (
        <div
          className={`detail-card settings-pane${cls("kb")}`}
          style={{ order: 13 }}
        >
          {sh4(
            "kb",
            _t("settings.panel.keyboardShortcuts", {
              fallback: "Keyboard Shortcuts",
            }),
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 12px",
              fontSize: FS.s11,
              marginTop: 6,
            }}
          >
            {[
              [
                "⎋ Escape",
                _t("settings.kb.escape", {
                  fallback: "Close modal / popup / search",
                }),
              ],
              [
                "⌃↵ Ctrl+Enter",
                _t("settings.kb.ctrlEnter", { fallback: "Save current form" }),
              ],
              [
                "⌥N Alt+N",
                _t("settings.kb.altN", {
                  fallback: "New item in current section",
                }),
              ],
              [
                "⌥T Alt+T",
                _t("settings.kb.altT", {
                  fallback: "Open Log Time from anywhere",
                }),
              ],
              [
                "⌥F Alt+F",
                _t("settings.kb.altF", { fallback: "Focus global search" }),
              ],
            ].map(([k, d]) => (
              <div key={k} style={{ display: "contents" }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: FS.s10,
                    color: "var(--ac)",
                    fontWeight: 600,
                  }}
                >
                  {k}
                </span>
                <span style={{ color: "var(--ts)" }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Phase M+2c — User preferences inline content lives under "Personal
    Preferences" (top-level header is declared at order:10 in the root
    return). The standalone "User preferences" setSecH was removed in
    M+2c since the new IA renames and groups it. */}
      {(
        <AppearanceFontsPanel
          cls={cls}
          sh4={sh4}
          theme={theme}
          textSize={textSize}
          setTextSize={setTextSize}
          autoNightSwitch={autoNightSwitch}
          setAutoNightSwitch={setAutoNightSwitch}
          dayStart={dayStart}
          setDayStart={setDayStart}
          nightStart={nightStart}
          setNightStart={setNightStart}
          themes={themes}
          appFont={appFont}
          setAppFont={setAppFont}
          toast={toast}
        />
      )}
      {/* Phase 44 — panel renders for all; firm-branding sub-parts gate on _meAuthIsAdmin inside. */}
      {(
      // Phase 45 — .dc sets overflow-x:auto (which computes overflow-y:auto
      // too), turning this panel into a scroll container that would trap the
      // theme editor's sticky preview here instead of the .C scroll area.
      // Neutralise it — this panel's content is responsive and never h-scrolls.
      <div
        className={`detail-card settings-pane${cls("theme")}`}
        style={{ order: 31, overflowX: "visible" }}
      >
        {sh4(
          "theme",
          _t("settings.panel.visualIdentity", { fallback: "Visual identity" }),
        )}
        {/* 204 Part Three §2.1 [Laurent: "the one that matters is last"] —
            THE FIRM'S IDENTITY COMES FIRST: the Branding block (Firm Name ·
            Main colour · Logo) MOVED from the foot of this pane to its head,
            unchanged but for the side its rule sits on. §A.3 keeps: the Main
            colour swatch stays under « Firm Name ». */}
        {/* Phase 44 D2 fix — VISIBILITY gates on _isAdminHere (the simulation-
            aware / token-holder admin), NOT _meAuthIsAdmin (the real logged-in
            actor). Impersonation swaps the token, so _isAdminHere matches what
            the server enforces; per SettingsView.jsx:297-302 visibility must use
            it. Hides the firm-branding surface from a real non-admin AND from an
            admin impersonating a non-admin. */}
        {_isAdminHere && (
        <div
          style={{
            /* 204 Part Three §2.1 — the block sat LAST with a rule above it;
               it sits FIRST now and the rule moves below it. */
            borderBottom: "1px solid var(--br)",
            marginBottom: 12,
            paddingBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".4px",
              color: "var(--tm)",
              marginBottom: 8,
            }}
          >
            {_t("settings.subhead.branding", { fallback: "Branding" })}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
              gap: 12,
            }}
          >
            <div>
              <div className="field">
                <label>
                  {_t("settings.label.firmName", { fallback: "Firm Name" })}
                </label>
                <input autoComplete="off" /* 207 §18 */
                  className="field-input no-contact-autofill"
                  value={firmName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFirmName(v);
                    saveFirmSettings &&
                      saveFirmSettings({
                        firm: { ...(firmSettings?.firm || {}), name: v },
                      }).catch(() => {});
                  }}
                  placeholder={_t("settings.placeholder.firmName", {
                    fallback: "Your firm name",
                  })}
                />
              </div>
              {/* 204 §A.3 [RULED] — the firm's main colour, under its name: the
                  Day theme's `--brand` through the theme route's own PATCH. See
                  FirmMainColourField at module scope. */}
              <FirmMainColourField
                themes={themes || []}
                setThemes={setThemes || (() => {})}
                firmSettings={firmSettings}
                toast={toast}
                t={_t}
              />
              <div className="field">
                <label>{_t("settings.label.logo", { fallback: "Logo" })}</label>
                {firmLogo ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <img
                      src={firmLogo}
                      alt="Logo"
                      style={{
                        maxHeight: 32,
                        maxWidth: 100,
                        objectFit: "contain",
                        background: "var(--bc)",
                        borderRadius: RAD.r4,
                        padding: 2,
                        border: "1px solid var(--br)",
                      }}
                    />
                    <button
                      className="btn btn-danger"
                      style={{
                        padding: "2px 6px",
                        fontSize: FS.s9,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                      onClick={() => {
                        setFirmLogo(null);
                        saveFirmSettings &&
                          saveFirmSettings({ logo: null }).catch(() => {});
                        toast(
                          _t("settings.toast.logoRemoved", {
                            fallback: "Logo removed",
                          }),
                        );
                      }}
                    >
                      <X size={10} />
                      {_t("settings.btn.remove", { fallback: "Remove" })}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 4 }}>
                    <label
                      className="btn btn-ghost"
                      style={{
                        padding: "3px 8px",
                        fontSize: FS.s10,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Upload size={10} />
                      {_t("settings.btn.uploadLogo", {
                        fallback: "Upload Logo",
                      })}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 512000) {
                            toast(
                              _t("settings.toast.fileTooLarge", {
                                fallback: "File too large (max 500KB)",
                              }),
                            );
                            e.target.value = "";
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            const dataUrl = (ev.target as any).result;
                            setFirmLogo(dataUrl);
                            saveFirmSettings &&
                              saveFirmSettings({ logo: dataUrl }).catch(
                                () => {},
                              );
                            toast(
                              _t("settings.toast.logoUploaded", {
                                fallback: "Logo uploaded",
                              }),
                            );
                          };
                          reader.readAsDataURL(file);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <span
                      style={{
                        fontSize: FS.s9,
                        color: "var(--tm)",
                        alignSelf: "center",
                      }}
                    >
                      {_t("settings.help.logoSizes", {
                        fallback: "PNG, JPG, SVG — max 500KB",
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* App window color relocated to the colour-editing subset above (Phase 44 D3). */}
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              className="btn btn-ghost"
              style={{ padding: "3px 10px", fontSize: FS.s10 }}
              onClick={() => {
                setFirmLogo(null);
                setFirmName("Legal Practice Manager");
                saveFirmSettings &&
                  saveFirmSettings({
                    logo: null,
                    firm: {
                      ...(firmSettings?.firm || {}),
                      name: "Legal Practice Manager",
                    },
                  }).catch(() => {});
                toast(
                  _t("settings.toast.brandingReset", {
                    fallback: "Branding reset to defaults",
                  }),
                );
              }}
            >
              {_t("settings.btn.resetBranding", {
                fallback: "Reset branding to defaults",
              })}
            </button>
          </div>
        </div>
        )}
        {/* 204 Part Three §2.2 [Laurent: "the two chrome colours sit together
            under ONE heading with ONE sentence between them, below it"] — THE
            BROWSER'S OWN CHROME, in one place under the firm's identity: the
            firm's card (WindowChromeCard — the window bar colour, its release
            to the theme, the window title, Save; admin, moved out of
            ThemeManager where it sat between Custom push and the library).
            ONE heading, ONE sentence (two new keys, three rows each — words a
            judgement, Laurent overrules at the glance); the card's own title
            and note are gone, their rows stay seeded and unread (the seed only
            inserts). §2.3 [MERGE, labelled]: the personal « Browser bar
            colour » that sat under the card is gone too — one setting. */}
        <div
          style={{
            borderBottom: "1px solid var(--br)",
            marginBottom: 12,
            paddingBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".4px",
              color: "var(--tm)",
              marginBottom: 8,
            }}
          >
            {_t("settings.subhead.chrome", { fallback: "Browser bar & window title" })}
          </div>
          <p
            style={{
              fontSize: FS.s10,
              color: "var(--tm)",
              margin: "0 0 8px",
              lineHeight: 1.55,
            }}
          >
            {_t("settings.help.chrome", {
              fallback:
                "These tint the browser's own bar — the tab and address bar, the installed app's title bar — and name its window; they are never the app's colour, which is « Main colour » above.",
            })}
          </p>
          {_isAdminHere && (
            <WindowChromeCard
              firmSettings={firmSettings}
              saveFirmSettings={saveFirmSettings || (async () => undefined)}
              toast={toast}
            />
          )}
        {/* Phase 44 D3 — App window color (per-user pref) relocated here so it
            renders for ALL users, alongside the palette editor.
            204 §A.5 [RULED — Laurent, 2026-09-22] — REWORDED so neither the
            label nor the help can be read as the firm's colour: this is the
            browser's address bar on mobile and the installed app's title bar,
            a per-user preference; the firm's colour is « Main colour » under
            Branding (§A.3). New keys (`…appWindowColor2`), three rows each —
            the seed only inserts, so a changed string is a new key. */}
        {/* 204 §2.3 [MERGE — LABELLED, Laurent rules at the glance]: the
            per-user « Browser bar colour » swatch stood here; the firm's
            window bar colour is the ONE setting now (see App.tsx's meta
            effect). Its rows (`settings.label.appWindowColor2`,
            `settings.help.appWindowColor2`) stay seeded and unread. */}
        </div>
        {_meAuthIsAdmin ? (
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              margin: "4px 0 10px",
              lineHeight: 1.5,
            }}
          >
            {_t("settings.theme.adminBlurb", {
              fallback:
                "Manage the firm-wide theme library, pin Day/Night/Custom defaults, and push the Custom theme firm-wide for a window.",
            })}
          </p>
        ) : (
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              margin: "4px 0 10px",
              lineHeight: 1.5,
            }}
          >
            {_t("settings.theme.userBlurbV2", {
              fallback:
                "Create and edit your own personal colour themes below, export/import them, and pick any theme with the Personal slot on the sidebar theme toggle. Slot pinning, push, and firm branding are admin-only.",
            })}
          </p>
        )}
        <ThemeManager
          themes={themes || []}
          setThemes={setThemes || (() => {})}
          firmSettings={firmSettings}
          saveFirmSettings={saveFirmSettings}
          toast={toast}
          admin={_isAdminHere}
          viewerId={_perms.viewer?.id ?? null}
          selectedThemeId={personalTheme ?? null}
          onUseTheme={(id: any) => {
            // D6 — set the durable per-user pick, then apply it (suspends auto).
            setPersonalTheme && setPersonalTheme(id);
            setAutoNightSwitch(false);
            setTheme(id);
          }}
        />
      </div>
      )}
      {/* Phase M+2c — Custom Lists → Taxonomies. Inner card sets its own
    flex order (now 81 to land beneath the "Custom Lists" header at
    order 80). Phase auth-c2 Stage 3 — gated by settings.customLists.taxonomies. */}
      {sg("settings.customLists.taxonomies") && (
        <SettingsTaxonomies
          isAdmin={_meAuthIsAdmin}
          toast={toast}
          focusSeq={settingsFocus?.pane === "taxonomies" ? settingsFocus.seq : 0}
        />
      )}
      {/* Phase 11 — model-document templates (Custom Lists, order 82). Self-gates
    to settings.customLists.docTemplates server-side; FE gate added Stage 3. */}
      {sg("settings.customLists.docTemplates") && (
        <DocTemplatesPanel
          toast={toast}
          collapsed={!panOpen["docTpl"]}
          onToggle={() => tog("docTpl")}
        />
      )}
      {/* Phase 14 — Rates admin. Four cards (order 71–74) land beneath the
    "Rates" section header at order 70. Phase auth-c2 Stage 3 — gated
    per-card by settings.rates.{legalInterest,commercialInterest,
    listingFees,proceduralCompensation}; mount now shows if ANY of those
    are held (the four cards self-gate inside). */}
      {(sg("settings.rates.legalInterest") ||
        sg("settings.rates.commercialInterest") ||
        sg("settings.rates.listingFees") ||
        sg("settings.rates.proceduralCompensation")) && (
        <SettingsRates isAdmin={_meAuthIsAdmin} toast={toast} />
      )}
      {/* Phase 35 (C4 / D5) — per-mode (read/review/write) doc-billing activity
          allowlist. Admin-only (server enforces ADMIN_ONLY_FIELD); lands under
          the Rates section header (order 75). */}
      {_isAdminHere && (
        <DocBillingActivitiesPanel isAdmin={_isAdminHere} toast={toast} />
      )}
      {/* Phase 35 (C5 / D6) — invoice e-export format picker (Peppol). Admin-only
          (server enforces ADMIN_ONLY_FIELD); transmission stays download-only. */}
      {_isAdminHere && (
        <InvoiceExportFormatPanel isAdmin={_isAdminHere} toast={toast} />
      )}
      {/* Phase M+2c — Users & Roles → Roles & Permissions admin UI.
    Replaces the legacy permMx editor and the M+2b stub copy that
    promised a "Gate 4b" delivery. Gated on simulated-identity admin —
    under "Viewing as" a non-admin the panel hides, matching what that
    role would see. Header simulator stays actor-based as escape hatch. */}
      {/* 80 §3 — the firm-admin fallback §4 of brief 78 promises in prose, given
          the screen it never had. Admin-only on the SIMULATED identity, like
          every other panel in this family, so "Viewing as" a non-admin hides it
          and simulation shows exactly what that role would see. */}
      {/* 81 §8 — brief 80 §3's panel SUPERSEDED. §8: "an admin-gated read of a
          colleague's factors, and removal one row at a time. That is a list,
          not an incident screen, and this brief makes it one." The incident
          screen carries what that one did (the factors, the removal) plus where
          and when they have been used, one act that revokes everything and ends
          every session, and the per-person cut-off. `AuthFactorsAdminPanel` is
          no longer mounted — 80's route it called is still there and still
          used, by this panel. */}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("authAdmin")}`}
          style={{ order: 53 }}
        >
          {sh4(
            "authAdmin",
            _t("settings.panel.authIncident", {
              fallback: "Sign-in credentials and access (a colleague's)",
            }),
          )}
          <AuthIncidentPanel
            collaborators={co.filter((c: CollaboratorLike) => !c.coType || c.coType === "internal")}
            isAdmin={_isAdminHere}
            killed={_killState?.people || []}
            onKillChange={_reloadKillState}
            toast={toast}
          />
        </div>
      )}
      {/* BRIEF 103 §2c — THE INVITATION POLICY, IN THE BLOCK THAT ALREADY EXISTS.
          Same admin gate and the SAME SOURCE of it as its two siblings above and
          below — `_isAdminHere`, the simulated identity, so "Viewing as" a
          non-admin hides this exactly as it hides the incident screen. The
          settings it writes have been admin-only at the route since 83 §3(4) and
          have had no screen at all since; §2a's "they were split only because the
          screen was never built" is this mount. */}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("inviteWindow")}`}
          style={{ order: 55 }}
        >
          {sh4(
            "inviteWindow",
            _t("settings.panel.inviteWindow", {
              fallback: "Enrolment invitations (how long, and what proves who turned up)",
            }),
          )}
          <InvitationPolicyPanel isAdmin={_isAdminHere} toast={toast} />
        </div>
      )}
      {/* 81 §8 — the panic switch, its own panel because it is its own act. */}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("killFirm")}`}
          style={{ order: 54 }}
        >
          {sh4(
            "killFirm",
            _t("settings.panel.killFirm", { fallback: "Close sign-in for the whole firm" }),
          )}
          <FirmKillSwitchPanel
            isAdmin={_isAdminHere}
            state={_killState}
            onChange={_reloadKillState}
            toast={toast}
          />
        </div>
      )}
      {_isAdminHere && (
        <div
          className={`detail-card settings-pane${cls("rolesPerm")}`}
          style={{ order: 52 }}
        >
          {sh4(
            "rolesPerm",
            _t("settings.panel.rolesPermissions", {
              fallback: "Roles & Permissions",
            }),
          )}
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              margin: "0 0 10px",
              lineHeight: 1.5,
            }}
          >
            {_t("settings.desc.rolesPermissions", {
              fallback:
                "Manage role display labels, rate ratios, firm-wide visibility, and the per-section permission defaults. Use the per-role editor for one role at a time, or the matrix view to compare and tweak across all roles. System presets (Partner, Associate, …) are editable but cannot be deleted; create custom roles via Duplicate.",
            })}
          </p>
          <RolesAdminPanel toast={toast} isAdmin={_meAuthIsAdmin} />
          {/* Phase 24 (C2) — per-surface scope editor (replaces the per-role
              firm-wide-visibility flag). Admin-only. */}
          {_meAuthIsAdmin && (
            <div style={{ marginTop: 18 }}>
              <h4 style={{ margin: "0 0 6px", fontSize: FS.s13 }}>
                {_t("settings.surfaceScopes.title", {
                  fallback: "Surface scopes (which cases each surface shows)",
                })}
              </h4>
              <SurfaceScopesPanel toast={toast} isAdmin={_meAuthIsAdmin} />
            </div>
          )}
          {/* Phase 37 (piece 2) — Case Team rights switch-off list. Admin-only. */}
          {_meAuthIsAdmin && (
            <div style={{ marginTop: 18 }}>
              <h4 style={{ margin: "0 0 6px", fontSize: FS.s13 }}>
                {_t("settings.caseTeamRights.title", {
                  fallback: "Case Team rights (what a responsible partner may delegate)",
                })}
              </h4>
              <CaseTeamRightsPanel toast={toast} isAdmin={_meAuthIsAdmin} />
            </div>
          )}
        </div>
      )}
      {/* Phase auth-c2 Stage 3 — fixed the mis-gate. The block below
          previously rode caseView.financials (a case-internal key) for
          firm-wide invoice settings. It now runs iff the user holds at
          least one Billing panel gate OR the Folder Templates panel
          gate; each panel inside is then individually gated with sg(). */}
      {(_SG.billing || sg("settings.integrations.folderTpl")) && (
        <BillingFolderPanels
          cls={cls}
          sh4={sh4}
          ex={ex}
          toast={toast}
          firmLogo={firmLogo}
          firmName={firmName}
          firmSettings={firmSettings}
          saveFirmSettings={saveFirmSettings}
          applyFirmSettings={applyFirmSettings}
          sg={sg}
        />
      )}
    </div>
  );
};

export default SettingsView;
